// The pose engine is a 140 KB bundle from another host. It used to be a static
// import, which meant nothing on any screen could respond until it arrived.
// Now it loads when a camera is about to be used (preloadModel), and the
// service worker keeps it on the device after that.
const VISION_URL = 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.18';
let visionMod = null;
const loadVision = () => (visionMod ??= import(VISION_URL));

import { asksForFilm } from '../shared/film-intent.js';
import { createShotDetector, summarize, TUNABLES, elementFor, sessionElement, createPoseTracker, createPlayerLock, createLandmarkSmoother, rimDistance, eyesOnRim } from '../scoring.js';
import * as lockcrop from './lockcrop.js';
import { createTrackClient } from './trackclient.js';
import { createCadence, delegateVerdict } from './trackmath.js';
import { createHandTracker } from './handtrack.js';
import { probe } from './trackprobe.js';
import * as elements from './elements.js';
import { mountCompanion, companionSVG } from './companion.js';
import { byId as companionById } from '../shared/companions.js';
import { createRadio } from './radio.js';
import * as vault from './vault.js';
/* Feature modules. Each one owns its markup inside one root element and one
 * css file, and gets the same context object at boot: see mountModules(). */
import * as ui from './ui.js';
import * as account from './account.js';
import * as scouts from './scouts.js';
import * as profile from './profile.js';
import * as nutrition from './nutrition.js';
import * as media from './media.js';
import * as search from './search.js';
import * as calendar from './calendar.js';
import * as social from './social.js';
import * as health from './health.js';
import * as career from './career.js';
import * as skills from './skills.js';
import * as game from './game.js';
import * as posemetrics from './posemetrics.js';
import { createBodyCapture } from '../shared/bodyparts.js';
import * as bodymap from './bodymap.js';
import { createConversation, prefetchSession } from './voice.js';
import { ambience } from './ambience.js';
import * as icons from './icons.js';
import { mountCredits } from './credits.js';

/* =========================================================================
 *  SPEECH OUT - the single seam
 *  Every spoken word goes through speak()/stopSpeaking(). To move to Pipecat
 *  (or Cloudflare Aura TTS), replace these two bodies with a websocket send
 *  and cancel. Nothing else in the app calls SpeechSynthesis.
 * ========================================================================= */
let voices = [];
function loadVoices() { voices = window.speechSynthesis?.getVoices() ?? []; }
loadVoices();
if ('speechSynthesis' in window) window.speechSynthesis.onvoiceschanged = loadVoices;

/* The companion speaks with Deepgram Aura on Workers AI, the same voice family
 * the Pipecat agents use, so it sounds like a person instead of a browser.
 * The browser's own synthesizer stays as the fallback: if the request fails,
 * the player still hears their companion, just not as well. */
let audioEl = null;                       // one element, reused, so iOS keeps the unlock
/* The voice agent inside Cloudflare (public/js/voicecf.js), while a conversation runs on it. Then
 * everything she says goes out through it, in its voice and its queue, so there is one voice and the
 * player can talk over any of it. voice.js hands it over (onAgent) and takes it back. */
let agent = null;
const agentLive = () => Boolean(agent?.live);
const ttsCache = new Map();               // url by voice+text, for a line said twice in a session
let ttsBroken = false;                    // one failure and we stop paying the latency

function canSpeak() {
  if (!S.settings.voiceEnabled) return false;
  return !(radio?.playing && S.settings.musicMode === 'quiet');
}
/** Strip anything that reads badly out loud. */
const sayable = (t) => String(t || '')
  .replace(/[*_#`>]/g, '')
  .replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}]/gu, '')
  .replace(/\s+/g, ' ').trim();

async function auraUrl(text) {
  const key = `${S.settings.voice || 'aura-arcas'}|${text}`;
  if (ttsCache.has(key)) return ttsCache.get(key);
  const r = await fetch('/api/speak', {
    method: 'POST', headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ text, voice: S.settings.voice }),
  });
  if (!r.ok) throw new Error(`voice ${r.status}`);
  const url = URL.createObjectURL(await r.blob());
  if (ttsCache.size > 60) { URL.revokeObjectURL(ttsCache.values().next().value); ttsCache.delete(ttsCache.keys().next().value); }
  ttsCache.set(key, url);
  return url;
}

function playUrl(url, { onStart, onEnd } = {}) {
  audioEl ??= new Audio();
  audioEl.onended = audioEl.onerror = audioEl.onplaying = null;
  audioEl.pause();
  audioEl.src = url;
  audioEl.onplay = () => { ambience.onHerSpeechStart(); radio?.duck(true); onStart?.(); };
  let finished = false;
  const done = () => { if (finished) return; finished = true; clearTimeout(watch); ambience.onHerSpeechEnd(); radio?.duck(false); onEnd?.(); };
  audioEl.onended = done;
  audioEl.onerror = done;
  // A clip that never starts (an empty or broken file) leaves the element
  // "not paused" for good, which reads as her still talking: the mic stays
  // shut and the film commentary waits forever. Four seconds without a frame
  // of sound counts as finished.
  let watch = setTimeout(() => { if (!finished && !audioEl.currentTime) { try { audioEl.pause(); } catch {} done(); } }, 4000);
  audioEl.onplaying = () => { clearTimeout(watch); watch = setTimeout(() => { if (!finished && audioEl.paused) done(); }, 30000); };
  return audioEl.play().catch(() => { done(); });
}

/** The browser's voice, kept as the fallback rather than the default. */
function browserSpeak(text, { onStart, onEnd } = {}) {
  if (!('speechSynthesis' in window)) { onEnd?.(); return; }
  window.speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(text);
  const PREFERRED = ['Google US English', 'Ava', 'Zoe', 'Samantha', 'Allison', 'Nicky', 'Karen', 'Daniel'];
  const picked = PREFERRED.map((n) => voices.find((v) => v.name.includes(n) && v.lang.startsWith('en'))).find(Boolean);
  if (picked) u.voice = picked;
  u.rate = 0.98; u.pitch = 1.02;
  u.onstart = () => { ambience.onHerSpeechStart(); radio?.duck(true); onStart?.(); };
  u.onend = u.onerror = () => { ambience.onHerSpeechEnd(); radio?.duck(false); onEnd?.(); };
  window.speechSynthesis.speak(u);
}

function speak(text, { onStart, onEnd: after, kind = 'line' } = {}) {
  noteSaid(text);
  const onEnd = () => { lastReplyEndAt = Date.now(); after?.(); };
  const clean = sayable(text);
  if (!clean || !canSpeak()) { onEnd?.(); return; }
  // On the voice agent: the same line, in its voice and in order. A newer training reaction
  // replaces one still waiting, so a fast run of shots never leaves her talking about old ones.
  if (agentLive()) { onStart?.(); agent.say(clean, { kind, replace: kind === 'reaction' || kind === 'film' }).then(onEnd, onEnd); return; }
  if (ttsBroken) return browserSpeak(clean, { onStart, onEnd });
  auraUrl(clean)
    .then((url) => playUrl(url, { onStart, onEnd }))
    .catch(() => { ttsBroken = true; browserSpeak(clean, { onStart, onEnd }); });
}
/**
 * A long text (the session report) spoken in pieces of whole sentences, one after another: the
 * speech route reads at most 700 characters (worker/speech.js MAX_CHARS) and a six to ten sentence
 * report was cut off mid sentence. onStart on the first piece, onEnd after the last.
 */
const SPEAK_PIECE = 600;
let speakLongGen = 0;   // stopSpeaking() moves it on, so the rest of a long text is not spoken after a stop
function speakLong(text, { onStart, onEnd, kind } = {}) {
  const gen = ++speakLongGen;
  const sentences = String(text || '').match(/[^.!?]+[.!?]+["')]?\s*|[^.!?]+$/g) || [];
  const pieces = [];
  for (const s of sentences) {
    if (pieces.length && (pieces[pieces.length - 1] + s).length <= SPEAK_PIECE) pieces[pieces.length - 1] += s;
    else pieces.push(s.slice(0, SPEAK_PIECE));
  }
  let i = 0;
  const next = () => {
    if (gen !== speakLongGen || i >= pieces.length) { onEnd?.(); return; }
    speak(pieces[i].trim(), { kind, onStart: i === 0 ? onStart : undefined, onEnd: next });
    i++;
  };
  next();
}
function stopSpeaking() {
  speakLongGen++;
  try { agent?.hush(); } catch {}
  try { audioEl?.pause(); } catch {}
  window.speechSynthesis?.cancel();
  radio?.duck(false);
}

/**
 * Streaming variant: sentences are synthesized and queued as they arrive, so
 * the companion starts talking about a second in while the rest is still being
 * written. Returns { push(text), end() }; onStart/onEnd fire once overall.
 */
function speakStream({ onStart, onEnd } = {}) {
  if (agentLive() && canSpeak()) return agentStream({ onStart, onEnd });
  const enabled = canSpeak();
  let buffer = '', ended = false, started = false, playing = false, finished = false;
  const queue = [];
  if (enabled) replyInFlight++;
  const release = () => { if (!finished) { finished = true; lastReplyEndAt = Date.now(); if (enabled) replyInFlight = Math.max(0, replyInFlight - 1); } };

  const finishIfDone = () => {
    if (ended && !queue.length && !playing) { release(); radio?.duck(false); onEnd?.(); }
  };
  const pump = () => {
    if (playing || !queue.length) { finishIfDone(); return; }
    playing = true;
    const next = queue.shift();
    Promise.resolve(next)
      .then((url) => new Promise((res) => {
        if (!url) return res();
        playUrl(url, { onStart: () => { if (!started) { started = true; onStart?.(); } }, onEnd: res });
      }))
      .catch(() => {})
      .then(() => { playing = false; pump(); });
  };
  let lastClean = '';
  const say = (sentence) => {
    const clean = sayable(sentence);
    if (!clean || !enabled) return;
    // The same sentence twice running is a stuck model, not a point being made.
    if (clean.toLowerCase() === lastClean) return;
    lastClean = clean.toLowerCase();
    if (ttsBroken) { browserSpeak(clean, { onStart: () => { if (!started) { started = true; onStart?.(); } } }); return; }
    // Start the request now, play it in order later.
    queue.push(auraUrl(clean).catch(() => { ttsBroken = true; return null; }));
    pump();
  };

  let spoke = false;
  return {
    /** A line said before the reply, as it is: the acknowledgement. */
    lead(text) { say(text); },
    push(text) {
      buffer += text;
      let m;
      // The first thing she says goes as soon as there is a clause of it: a
      // comma after 18 characters is enough to start the voice. Waiting for
      // the whole first sentence cost most of a second on the big model.
      while ((m = /^[\s\S]*?[.!?](?:["')\]]+)?(?=\s|$)/.exec(buffer)) || (!spoke && (m = /^[\s\S]{18,}?[,;:](?=\s)/.exec(buffer))) || (buffer.length >= 60 && (m = /^[\s\S]{40,}?[,;:](?=\s)/.exec(buffer)))) {
        const sentence = m[0]; buffer = buffer.slice(sentence.length); say(sentence); spoke = true;
        if (!buffer.trim()) break;
      }
    },
    end() {
      if (buffer.trim()) say(buffer);
      buffer = ''; ended = true;
      if (!enabled) { release(); onEnd?.(); return; }
      finishIfDone();
    },
  };
}

/**
 * speakStream on the voice agent: the same cut (first clause, then sentences), each piece a 'say' in
 * her voice, in order, so a reply that streams from another route (the coach, the plan) sounds and
 * stops exactly like one from the agent's own brain turns.
 */
function agentStream({ onStart, onEnd } = {}) {
  let buffer = '', spoke = false, started = false, finished = false, last = Promise.resolve();
  replyInFlight++;
  const say = (piece) => {
    const clean = sayable(piece); if (!clean) return;
    noteSaid(clean);
    if (!started) { started = true; onStart?.(); }
    last = agent.say(clean, { kind: 'reply' });
  };
  const finish = () => {
    if (finished) return; finished = true;
    last.then(() => { replyInFlight = Math.max(0, replyInFlight - 1); lastReplyEndAt = Date.now(); onEnd?.(); });
  };
  return {
    lead(text) { say(text); },
    push(text) {
      buffer += text;
      let m;
      while ((m = /^[\s\S]*?[.!?](?:["')\]]+)?(?=\s|$)/.exec(buffer)) || (!spoke && (m = /^[\s\S]{18,}?[,;:](?=\s)/.exec(buffer))) || (buffer.length >= 60 && (m = /^[\s\S]{40,}?[,;:](?=\s)/.exec(buffer)))) {
        const sentence = m[0]; buffer = buffer.slice(sentence.length); say(sentence); spoke = true;
        if (!buffer.trim()) break;
      }
    },
    end() { if (buffer.trim()) say(buffer); buffer = ''; finish(); },
  };
}

/* ------------------------------------------------------------ acknowledgement
 * The instant a spoken turn arrives she says a short line ("Got it."), so the
 * player hears her react while the real answer is still being thought up. It
 * goes to the front of the reply's own sentence queue, so it plays first and
 * never talks over the answer. Each line is fetched through /api/speak once, in
 * her voice, when a conversation starts; the Worker keeps it in R2 and never
 * meters a repeat, and the browser keeps the audio, so it plays from memory.
 * A line that is not in memory yet is skipped rather than waited for. English
 * only: Aura speaks English. Short turns ("hey", "yo") get a quick answer and
 * no acknowledgement. Every line is registered with the echo guard.
 */
const ACKS = ['Got it.', 'Okay, let me think.', 'Hmm, good one.', 'Alright.', 'Let me see.', 'Good question.'];
const ackReady = new Set();
let lastAck = '';
function warmAcks() {
  if ((S.settings.language || 'en') !== 'en' || !canSpeak()) return;
  for (const line of ACKS) if (!ackReady.has(`${S.settings.voice}|${line}`)) auraUrl(line).then(() => ackReady.add(`${S.settings.voice}|${line}`)).catch(() => {});
}
function pickAck(said) {
  if ((S.settings.language || 'en') !== 'en' || String(said || '').trim().split(/\s+/).length <= 2) return null;
  const ready = ACKS.filter((l) => l !== lastAck && ackReady.has(`${S.settings.voice}|${l}`));
  if (!ready.length) return null;
  lastAck = ready[Math.floor(Math.random() * ready.length)];
  return lastAck;
}
/** Set by the conversation for the turn it hands to talk(); typed turns get no acknowledgement. */
let spokenTurn = null;
/** A reply the conversation started on Flux's EagerEndOfTurn for this exact turn (voice.js prepare). */
let earlyTurn = null;

/** POST to a streaming route; falls back to plain JSON when the server does not stream. */
async function streamApi(url, body, { onMeta, onText, onSaid, pre } = {}) {
  const r = await (pre || fetch(url, { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) }));
  if (!r.ok) { const j = await r.json().catch(() => ({})); throw Object.assign(new Error(j.error || `request failed (${r.status})`), { status: r.status }); }
  if (!(r.headers.get('content-type') || '').includes('text/event-stream')) {
    const j = await r.json();
    const text = j.say ?? j.reply ?? j.report ?? '';
    onMeta?.(j); onText?.(text);
    return { text, meta: j };
  }
  const reader = r.body.getReader(); const dec = new TextDecoder();
  let buf = '', full = '', meta = {};
  for (;;) {
    const { value, done } = await reader.read(); if (done) break;
    buf += dec.decode(value, { stream: true });
    const events = buf.split('\n\n'); buf = events.pop();
    for (const ev of events) {
      const type = /^event: (\w+)/m.exec(ev)?.[1]; const data = /^data: (.*)$/m.exec(ev)?.[1];
      if (!type || !data) continue;
      const d = JSON.parse(data);
      if (type === 'meta') { meta = d; onMeta?.(d); }
      else if (type === 'delta') { full += d.text; onText?.(d.text); }
      else if (type === 'said') onSaid?.(d.text);
      else if (type === 'error') throw new Error(d.message);
    }
  }
  return { text: full.trim(), meta };
}
/* ===================== END SPEECH SEAM ================================== */

const $ = (id) => document.getElementById(id);
/** Anything a player typed, or a model wrote, is text and never markup. */
const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
const api = async (url, opts) => {
  // Every request gets a deadline. Without one a stalled connection leaves a
  // button disabled and a panel spinning for as long as the browser allows.
  const ac = new AbortController();
  const timer = setTimeout(() => ac.abort(), 60_000);
  let r;
  try {
    r = await fetch(url, { headers: { 'content-type': 'application/json' }, signal: ac.signal, ...opts });
  } catch (e) {
    throw new Error(e.name === 'AbortError' ? 'That took too long. Check your connection and try again.' : e.message);
  } finally { clearTimeout(timer); }
  const body = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(body.error || `request failed (${r.status})`);
  return body;
};

const S = {
  settings: {}, config: {}, view: 'dashboard',
  sessionId: null, shots: [], pendingIndex: null, running: false, rim: null,
  facing: 'user',                 // 'user' is the mirror; 'environment' is the back camera on a tripod
  transcript: [],
};

// A deploy arrived in the background (sw.js posts hb-update once the new bundle is saved): offer a
// refresh, never in the middle of a session. Listening here, while the module first runs, because the
// message can arrive during startup and a listener added later misses it (2026-10-04).
if ('serviceWorker' in navigator) {
  let offered = false;
  navigator.serviceWorker.addEventListener('message', (ev) => {
    if (ev.data?.type !== 'hb-update' || offered) return;
    offered = true;
    const offer = () => (S.running ? setTimeout(offer, 30000) : toastAction('A new version of Hooper Buddy is ready.', 'Refresh', () => location.reload()));
    setTimeout(offer, 1500);
  });
  navigator.serviceWorker.startMessages();
}

let buddy = null, watcher = null, gateBall = null, talkBall = null;
let radio = null;
let recorder = null;
const detector = createShotDetector();
/* Body Map (BODY-MAP.md): a rolling buffer of the player's landmarks
 * (bodyparts.js createBodyCapture). Each shot leaves one small snapshot of
 * numbers, never video or an image, sent to the player's own record in
 * batches with the movement reps. The session never waits on it. */
let bodyCap = null;
const bodyOut = { q: new Map(), seq: 0, pending: null, busy: null };   // q: session id -> { snaps, reps }
const bodyQ = (sid) => bodyOut.q.get(sid) || bodyOut.q.set(sid, { snaps: [], reps: [] }).get(sid);
function queueBody(sid, index, snapshot) {
  if (!sid || !Number.isInteger(index) || !snapshot) return;
  const q = bodyQ(sid);
  q.snaps.push({ ...snapshot, id: `${sid}:${index}` });
  if (q.snaps.length >= 8) flushBody();
}
function queueRep(sid, rep) {
  if (!sid || !rep?.kind || rep.kind === 'shot' || !rep.scores) return;
  const q = bodyQ(sid);
  q.reps.push({ kind: rep.kind, scores: rep.scores });
  if (q.reps.length >= 200) flushBody();
}
/** One batch. A batch that fails goes again exactly as it was (same seq), so the Worker never counts it twice. */
async function flushBody() {
  while (bodyOut.busy) await bodyOut.busy;
  if (!bodyOut.pending) {
    const hit = [...bodyOut.q].find(([, q]) => q.snaps.length || q.reps.length);
    if (!hit) return true;
    const [sid, q] = hit;
    bodyOut.pending = { sid, seq: ++bodyOut.seq, snaps: q.snaps.splice(0, 12), reps: q.reps.splice(0, 200) };
    if (!q.snaps.length && !q.reps.length) bodyOut.q.delete(sid);
  }
  const b = bodyOut.pending;
  bodyOut.busy = api(`/api/sessions/${b.sid}/bodymap`, { method: 'POST', body: JSON.stringify({ seq: b.seq, snaps: b.snaps, reps: b.reps }) })
    .then(() => { bodyOut.pending = null; return true; }, () => false)
    .finally(() => { bodyOut.busy = null; });
  return bodyOut.busy;
}
/**
 * Everything waiting, batch after batch. The Worker takes one batch per
 * player every 2 s, so a refused batch is tried again 2.1 s later, twice at
 * most; anything still left goes with the next flush.
 */
async function flushAllBody() {
  for (let i = 0, misses = 0; i < 30; i++) {
    if (!bodyOut.pending && ![...bodyOut.q.values()].some((q) => q.snaps.length || q.reps.length)) return true;
    if (await flushBody()) continue;
    if (++misses > 2) return false;
    await new Promise((r) => setTimeout(r, 2100));
  }
  return false;
}
let tracker = null, lastVideoTime = -1;

/* ------------------------------------------------------------ debug trace
 * ?debug=1 records every frame's detector features so thresholds can be tuned
 * against a real person. "Save trace" (or ending the session) posts it to the
 * server, which writes debug/trace-*.json. Off by default, costs nothing. */
const DEBUG = new URLSearchParams(location.search).has('debug');
const trace = { frames: [], events: [], shots: [], video: null, tunables: TUNABLES };
const MAX_TRACE_FRAMES = 6000;
let lastState = 'idle';
const frameTimes = [];

function recordFrame(now) {
  const f = detector.last;
  if (!f) return;
  if (trace.frames.length < MAX_TRACE_FRAMES) {
    trace.frames.push(f.skip
      ? [Math.round(f.t), f.skip]
      : [Math.round(f.t), f.state, +f.rise.toFixed(2), Math.round(f.elbowAngle),
         f.noseRise === null ? null : +f.noseRise.toFixed(2), +f.wristVis.toFixed(2), f.side]);
  }
  if (detector.state !== lastState) {
    trace.events.push([Math.round(now), `${lastState}->${detector.state}`]);
    lastState = detector.state;
  }
  frameTimes.push(now); if (frameTimes.length > 30) frameTimes.shift();
  const fps = frameTimes.length > 1 ? 1000 / ((now - frameTimes[0]) / (frameTimes.length - 1)) : 0;
  document.getElementById('debugHud').textContent = f.skip
    ? `${f.skip} · ${fps.toFixed(0)} fps`
    : `${detector.state} · ${f.side} · rise ${f.rise.toFixed(2)} · elbow ${f.elbowAngle.toFixed(0)}° · nose ${f.noseRise?.toFixed(2) ?? '?'} · vis ${f.wristVis.toFixed(2)} · ${fps.toFixed(0)} fps`;
}

async function saveTrace() {
  if (!DEBUG) return;
  try {
    const r = await api('/api/debug/trace', { method: 'POST', body: JSON.stringify(trace) });
    toast(`Trace saved: ${r.file} (${r.frames} frames, ${r.shots} shots)`);
  } catch (err) { toast(`Trace failed: ${err.message}`, true); }
}

/* ------------------------------------------------------------------ chrome */

function toast(msg, err = false) {
  const el = $('toast');
  el.textContent = msg;
  el.className = `toast show${err ? ' err' : ''}`;
  clearTimeout(toast.t);
  toast.t = setTimeout(() => (el.className = 'toast'), 3400);
}

/** Views a link may open directly. Anything else is ignored rather than trusted. */
/* ------------------------------------------------------------ the free demo
 * /app?demo=5: anyone, no account, five shots. The banner counts them, the
 * session ends itself after the fifth, the companion speaks her report on
 * those five (elbow, release, follow-through), and the gate offers the
 * waitlist or more shots. The sales page and the sales video promise exactly
 * this, so it has to be one tap from either. */
const DEMO_SHOTS = 5;
let demoMode = (() => { try { return /^(5|free|five)$/i.test(new URLSearchParams(location.search).get('demo') || ''); } catch { return false; } })();
let demoEnding = false, demoTaken = 0;
function paintDemo() {
  const b = $('demoBanner'); if (!b) return;
  b.hidden = !demoMode;
  if (demoMode) $('demoCount').textContent = `${Math.min(S.shots.length, DEMO_SHOTS)} of ${DEMO_SHOTS}`;
}

const LINKABLE = new Set(['body', 'scouts', 'dashboard', 'train', 'sessions', 'brain', 'vault', 'journal', 'nutrition',
  'health', 'career', 'skills', 'game', 'calendar', 'social', 'companion', 'music', 'settings']);

/**
 * Where a link asked to land. `/app?view=companion` and `/app#companion` both
 * work, so the sales page can drop somebody straight into the thing they came
 * to try instead of onto the camera gate.
 */
function requestedView() {
  try {
    const u = new URL(location.href);
    const asked = (u.searchParams.get('view') || u.hash.replace(/^#/, '')).trim().toLowerCase();
    return LINKABLE.has(asked) ? asked : null;
  } catch { return null; }
}

/**
 * A workout is not left by accident. While the camera is running the app stays
 * on Train: a stray tap on the rail, a misheard word, or the companion deciding
 * a different screen would be helpful must not pull somebody out of a session
 * and leave the camera scoring an empty court. There are exactly two ways out,
 * the End session button and asking the companion to end it, and both go
 * through endSession() so the report gets written either way.
 */
function lockedInTraining(view) {
  if (!S.running || view === 'train') return false;
  toast('Finish your session first. Tap End session, or ask me to end it.', true);
  return true;
}

function go(view) {
  if (lockedInTraining(view)) return;
  S.view = view;
  agent?.context({ view });
  // Keep the address bar honest, so a reload or a shared link lands in the same
  // place. replaceState rather than push: the rail is not browser history.
  try { if (LINKABLE.has(view)) history.replaceState(null, '', `${location.pathname}#${view}`); } catch {}
  document.querySelectorAll('.view').forEach((v) => v.classList.toggle('on', v.id === `v-${view}`));
  // Never leave the active destination hidden inside a closed section.
  const sec = document.querySelector(`.rail button[data-view="${view}"]`)?.closest('.rail-sec');
  if (sec?.classList.contains('closed')) setSection(sec.dataset.sec, true);
  document.querySelectorAll('.rail button[data-view], .tabbar button[data-view]').forEach((b) =>
    b.classList.toggle('on', b.dataset.view === view));
  if (view === 'sessions') renderSessions();
  if (view === 'brain') { renderBrain(); search.render(); }
  if (view === 'dashboard') renderDashboard();
  if (view === 'vault') renderVault();
  if (view === 'journal') renderJournal();
  if (view === 'companion') renderCompanion();
  if (view === 'music') renderMusic();
  if (view === 'nutrition') nutrition.render();
  if (view === 'calendar') calendar.render();
  if (view === 'social') social.render();
  if (view === 'health') health.render();
  if (view === 'career') career.render();
  if (view === 'skills') skills.render();
  if (view === 'body') bodymap.show();
  if (view === 'game') game.render();
  if (view === 'train') { posemetrics.render?.(); preloadModel().catch(() => {}); }
  // Her room and Train are where people talk: wake the model and her voice agent before the mic is tapped.
  if (view === 'companion' || view === 'train') warmCompanion();
  if (view === 'settings') { account.render(); profile.render(); scouts.renderVisibility(); }
  if (view === 'settings') mountCredits($('v-settings'));
  if (view === 'scouts') scouts.render();
  // Her room and Train are where talking starts: ask where the voice runs now, not on the tap.
  if (view === 'companion' || view === 'train') prefetchSession();
  // Leaving the room drops full screen with it, or the rail stays buried.
  if (view !== 'companion') roomFull(false);
  paintTalkChrome();
}

/* ---------------------------------------------------------- the room, big
 * Deliberately the app's own full screen rather than the Fullscreen API: that
 * one is blocked inside an iframe, refused on iOS Safari for anything that is
 * not a video, and it takes the rail away without putting anything back. This
 * behaves the same on a phone, a tablet and a desktop.
 */
function roomFull(on) {
  const room = document.querySelector('#v-companion .room');
  if (!room) return;
  room.classList.toggle('full', on);
  document.documentElement.classList.toggle('room-full', on);
  const label = $('roomFullLabel');
  if (label) label.textContent = on ? 'Exit full screen' : 'Full screen';
  for (const id of ['roomFull', 'roomFullSolo']) {
    const host = $(id);
    if (!host) continue;
    // icons.paint swaps the holder for an svg, so put a fresh holder back first.
    const old = host.querySelector('svg, [data-icon]');
    const fresh = document.createElement('i');
    fresh.setAttribute('data-icon', on ? 'collapse' : 'expand');
    fresh.setAttribute('data-size', id === 'roomFull' ? '16' : '18');
    fresh.setAttribute('aria-hidden', 'true');
    if (old) old.replaceWith(fresh); else host.prepend(fresh);
    icons.paint(host);
    host.title = on ? 'Exit full screen' : 'Full screen';
  }
}
const roomIsFull = () => Boolean(document.querySelector('#v-companion .room')?.classList.contains('full'));

/** One context object, passed to every feature module at boot. */
function mountModules() {
  const ctx = {
    api, streamApi, toast, ui, go, companionById,
    // The live sheet has its own mic button. It starts the same conversation
    // the talk bar does, and while a run is live every transcript is routed to
    // game.voiceLine first, so calling out plays logs them hands free.
    listen: () => listen(),
    listening: () => conversation.live,
    S,                                  // live settings + view state
    refresh: {
      journal: () => renderJournal(),
      settings: async () => { S.settings = await api('/api/settings'); },
      dashboard: () => renderDashboard(),
    },
  };
  [ui, account, profile, nutrition, media, search, calendar, social, health, career, skills, game, posemetrics, scouts, bodymap].forEach((mod) => {
    try { mod.mount?.(ctx); } catch (e) { console.error('module failed to mount', e); }
  });
  // The scout desk is on the rail only for a verified scout or agent account.
  api('/api/scouts/me').then((r) => { if (r?.scout) document.getElementById('railScouts')?.removeAttribute('hidden'); }).catch(() => {});
}

/* -------------------------------------------------------------- transcript */

/* The transcript only exists while a conversation is happening. It appears on
 * the first word of a turn and puts itself away once things go quiet, so it is
 * not a permanent panel sitting over the app doing nothing. */
const DOCK_QUIET_MS = 30000;
let dockTimer = null;
function dockShow(on) {
  const d = $('dock');
  d.classList.toggle('live', on);
  if (on) d.classList.remove('min');
  clearTimeout(dockTimer);
  if (on) dockTimer = setTimeout(() => $('dock').classList.remove('live'), DOCK_QUIET_MS);
}

const WHO = { you: 'YOU', coach: 'COMPANION', sys: 'SESSION' };
function dockRow(who) {
  const el = $('dockLines');
  const row = document.createElement('div');
  row.className = `tline ${who}`;
  row.innerHTML = `<span class="t">${WHO[who] || 'SESSION'} &middot; ${
    new Date().toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}</span><span class="txt"></span>`;
  el.appendChild(row);
  el.scrollTop = el.scrollHeight;
  return row.lastElementChild;
}

function transcribe(who, text) {
  S.transcript.push({ who, text, at: new Date() });
  // A session note on its own does not summon the panel; a spoken turn does.
  dockShow(who !== 'sys' || $('dock').classList.contains('live'));
  dockRow(who).textContent = text;
  $('dockLines').scrollTop = $('dockLines').scrollHeight;
  $('dockCount').textContent = S.transcript.length;
}

/**
 * The companion's side, written as it streams, so the transcript shows the
 * words while they are being spoken rather than all at once when it finishes.
 */
function transcribeStream(who = 'coach') {
  dockShow(true);
  const span = dockRow(who);
  let full = '';
  return {
    push(t) {
      full += t;
      span.textContent = full;
      $('dockLines').scrollTop = $('dockLines').scrollHeight;
      clearTimeout(dockTimer);
      dockTimer = setTimeout(() => $('dock').classList.remove('live'), DOCK_QUIET_MS);
    },
    end(text) {
      full = (text || full).trim();
      span.textContent = full;
      if (full) { S.transcript.push({ who, text: full, at: new Date() }); $('dockCount').textContent = S.transcript.length; }
      else span.parentElement.remove();
      $('dockLines').scrollTop = $('dockLines').scrollHeight;
    },
  };
}

/* ------------------------------------------------------- livestream feed */

function post(text, tone = 'note', who = null, full = false) {
  const feed = $('feed');
  const msg = document.createElement('div');
  msg.className = `msg ${tone}`;
  const name = who || (tone === 'coach' ? companionById(S.settings.companion).name : null);
  // Live feed stays short-form: first sentence only. The transcript keeps it all.
  const shown = full || text.length <= 140 ? text : (text.match(/^[^.!?]+[.!?]/)?.[0] ?? text.slice(0, 140) + '…');
  msg.innerHTML = `${name ? `<span class="who">${esc(name)}</span>` : ''}<span>${esc(shown)}</span>`;
  feed.appendChild(msg);
  while (feed.children.length > 9) feed.removeChild(feed.firstChild);
  if (tone === 'coach' && text) transcribe('coach', text);
  return msg;
}

/** Streams text into a feed chip until the first sentence ends. Feed stays short-form. */
function feedFirstSentence(chip, t) {
  if (chip === 'done') return chip;
  if (!chip) chip = post('', 'coach');
  const span = chip.lastElementChild;
  span.textContent += t;
  const m = /^[\s\S]*?[.!?](?=\s|$)/.exec(span.textContent);
  if (m && span.textContent.length > m[0].length) { span.textContent = m[0]; return 'done'; }
  if (m) return 'done';
  return chip;
}

/* ------------------------------------------------------------- dashboard */

async function renderDashboard() {
  const r = await api('/api/recall');
  const c = companionById(S.settings.companion);

  $('heroTag').textContent = `${c.name} the ${c.sport.toLowerCase()}`;
  if (!r.lifetime) {
    $('heroTitle').textContent = 'Lets get your first session in.';
    $('heroLine').textContent = `I'm ${c.name}. Point a camera at yourself, put some shots up, and I'll start learning your form.`;
  } else {
    const last = r.recent.at(-1);
    $('heroTitle').textContent = `${r.sessions} sessions in. Form is at ${last.form}.`;
    $('heroLine').textContent = `${c.blurb} Lifetime you're ${r.lifetime.makes} of ${r.lifetime.attempts}. Your strongest metric is follow-through at ${r.lifetime.follow}; elbow alignment at ${r.lifetime.elbow} is where the room is.`;
  }

  $('dashStats').innerHTML = r.lifetime ? `
    <div class="stat"><b>${r.sessions}</b><small>sessions</small></div>
    <div class="stat"><b>${r.lifetime.attempts}</b><small>attempts</small></div>
    <div class="stat"><b>${r.lifetime.makes}</b><small>makes</small></div>
    <div class="stat"><b>${Math.round((r.lifetime.makes / r.lifetime.attempts) * 100)}%</b><small>field</small></div>` : '';

  $('dashMetrics').innerHTML = r.lifetime ? ['elbow', 'release', 'follow'].map((k) => `
    <div style="margin-bottom:12px">
      <div style="display:flex;justify-content:space-between;font:600 11px/1 var(--mono);margin-bottom:6px">
        <span>${{ elbow: 'Elbow', release: 'Release', follow: 'Follow-through' }[k]}</span>
        <span style="color:var(--hot)">${r.lifetime[k]}</span></div>
      <div class="bar"><i style="width:${r.lifetime[k]}%"></i></div></div>`).join('')
    : '<span style="color:var(--mute);font-size:13px">No data yet.</span>';

  const last = r.recent?.at(-1);
  $('dashLast').textContent = last
    ? `${new Date(last.when).toLocaleDateString(undefined, { month: 'long', day: 'numeric' })} · ${last.makes} of ${last.attempts}, form ${last.form}. ${last.saidToYou.at(-1)?.slice(0, 150) ?? ''}`
    : 'Nothing logged yet.';

  $('dashCompanion').textContent = `${c.name} is a ${c.sport.toLowerCase()} with a face and strong opinions about your elbow. ${c.blurb}`;
  social.paintHero($('dashPoints'));
}

/* ------------------------------------------------------------------ train */

/**
 * Which pose model. ?model=heavy|lite wins. Without one, full, unless this
 * device once measured full running fast enough that heavy fits: see
 * benchModel. That verdict lives under one localStorage key.
 */
const MODEL_KEY = 'hb-pose-model';
const MODEL_OVERRIDE = (() => { const m = new URLSearchParams(location.search).get('model'); return m === 'heavy' || m === 'lite' ? m : null; })();
// A pick is measured again after 30 days: a browser update or a new GPU
// driver can move a device across the line either way.
const PICK_DAYS = 30;
const storedModel = () => { try { const m = JSON.parse(localStorage.getItem(MODEL_KEY) || 'null'); return m && Date.now() - (Number(m.at) || 0) < PICK_DAYS * 864e5 ? m.pick || null : null; } catch { return null; } };
// Heavy that failed to download or build on this page falls back to full.
let heavyFailed = false;
/**
 * Graphics chip (GPU) or processor (CPU) for the models. The GPU, unless this device has none worth
 * the name (software graphics: a pose took 3.1 s on the GPU path against 0.47 on the CPU, 2026-10-04)
 * or its GPU proves slow and a CPU trial proves faster (trackmath.js delegateVerdict, which also says
 * why only frames after the hands have settled count). The verdict is remembered for 30 days under a
 * new key: the first version (hb-delegate) could switch a device to the CPU on its hand building
 * frames, so its picks are not read. ?delegate=cpu|gpu overrides, for measuring. On the CPU the
 * models read a smaller picture (CPU_SIDE): 72 ms a pose at 1080p, 35 at 640 (this Mac).
 */
const MAXSIDE_OVERRIDE = Number(new URLSearchParams(location.search).get('maxside')) || 0;
const DELEGATE_OVERRIDE = (() => { const d = new URLSearchParams(location.search).get('delegate'); return d === 'cpu' ? 'CPU' : d === 'gpu' ? 'GPU' : null; })();
const DELEGATE_KEY = 'hb-delegate-v2';
const CPU_SIDE = 640, SETTLE_FRAMES = 60;
const storedDelegate = () => { try { const m = JSON.parse(localStorage.getItem(DELEGATE_KEY) || 'null'); return m && Date.now() - (Number(m.at) || 0) < 30 * 864e5 ? m : null; } catch { return null; } };
const saveDelegate = (m) => { try { localStorage.setItem(DELEGATE_KEY, JSON.stringify({ ...m, at: Date.now() })); } catch { /* decided again next visit */ } };
try { localStorage.removeItem('hb-delegate'); } catch { /* nothing to clear */ }
let gpuName = '';
let softwareGl = null;
function softwareGraphics() {
  if (softwareGl !== null) return softwareGl;
  try {
    const gl = document.createElement('canvas').getContext('webgl2') || document.createElement('canvas').getContext('webgl');
    const ext = gl && gl.getExtension('WEBGL_debug_renderer_info');
    gpuName = gl ? String(gl.getParameter(ext ? ext.UNMASKED_RENDERER_WEBGL : gl.RENDERER) || '') : 'none';
    softwareGl = !gl || /swiftshader|llvmpipe|softpipe|software|basic render/i.test(gpuName);
  } catch { softwareGl = true; }
  return softwareGl;
}
let switchedTo = null, trialGpuMs = null, failedOver = false;   // this visit's switch, and the GPU time that led to a CPU trial
const pickDelegate = () => DELEGATE_OVERRIDE || switchedTo || storedDelegate()?.pick || (softwareGraphics() ? 'CPU' : 'GPU');
let builtDelegate = null, delegateTimes = [], delegateDone = false, delegateFrames = 0, handsSettled = false;
/** One frame on the delegate in use: once the hands have settled and there are enough, decide (see above). */
function benchDelegate(ms, handsRan) {
  if (delegateDone || DELEGATE_OVERRIDE) return;
  delegateFrames++;
  if (handsRan) handsSettled = true;
  if (!handsSettled && delegateFrames < SETTLE_FRAMES) return;   // hands still building, or none here
  if (Number.isFinite(ms)) delegateTimes.push(ms);
  const v = delegateVerdict({ on: builtDelegate, times: delegateTimes, gpuMs: trialGpuMs });
  if (!v) return;
  delegateDone = true;
  if (v.next === 'try-CPU') { trialGpuMs = v.median; switchDelegate('CPU'); return; }
  if (failedOver) return;
  saveDelegate({ pick: v.next, gpuMs: builtDelegate === 'GPU' ? +v.median.toFixed(1) : trialGpuMs, cpuMs: builtDelegate === 'CPU' ? +v.median.toFixed(1) : null });
  if (v.next !== builtDelegate) switchDelegate(v.next);
}
/** Rebuild the tracker on the other delegate without ending the session: frames wait while it builds. */
async function switchDelegate(pick) {
  console.warn(`tracking: switching from ${builtDelegate} to ${pick}`);
  if (S.running) toast(pick === 'CPU' ? 'Trying a faster tracking mode for this device.' : 'Switching tracking back to the graphics chip.');
  switchedTo = pick;
  try { tracker?.close(); } catch {}
  tracker = null; trackerReady = null;
  try { await preloadModel(); delegateTimes = []; delegateDone = false; delegateFrames = 0; handsSettled = false; }
  catch (e) { console.error('tracking switch', e); toast('Tracking could not restart. End the session and start again.', true); }
}
const POSE_MODEL = () => MODEL_OVERRIDE || (!heavyFailed && storedModel() === 'heavy' ? 'heavy' : 'full');
let builtModel = null;

/**
 * Model auto select. Budget: a frame every 33 ms at 30 fps. Heavy is taken to
 * cost up to three times what full does (an assumption, not measured here).
 * Where the models run on the page (no tracking worker), full has to run at a
 * median of 10 ms or under before heavy is picked, as it always did. In the
 * worker the page never waits on a model, so the test is whether heavy and
 * the hand model together still fit in 80 percent of a frame: three times the
 * full model's median plus the hand model's median, at most 26 ms. Measured
 * once per device, on BENCH.FRAMES frames after BENCH.SKIP warm up ones (the
 * first frames compile shaders and are slow), stored under MODEL_KEY and
 * measured again after PICK_DAYS. An override skips it. The pick lands on the
 * next camera start: the models already built keep running this one.
 */
const BENCH = { SKIP: 10, FRAMES: 30, HEAVY_UNDER_MS: 10, WORKER_HEAVY_FIT_MS: 26 };
let benchTimes = [], benchHands = [], benchDone = false;
function benchModel(ms, handsMs, path) {
  if (benchDone || MODEL_OVERRIDE || builtModel !== 'full' || storedModel()) return;
  if (Number.isFinite(handsMs)) benchHands.push(handsMs);
  if (!Number.isFinite(ms)) return;
  benchTimes.push(ms);
  if (benchTimes.length < BENCH.SKIP + BENCH.FRAMES) return;
  benchDone = true;
  const med = (a) => { const s = a.slice().sort((x, y) => x - y); return s.length ? s[Math.floor(s.length / 2)] : 0; };
  const median = med(benchTimes.slice(BENCH.SKIP));
  const handsMedian = med(benchHands.slice(Math.min(BENCH.SKIP, Math.floor(benchHands.length / 2))));
  const pick = path === 'worker'
    ? (3 * median + handsMedian <= BENCH.WORKER_HEAVY_FIT_MS ? 'heavy' : 'full')
    : (median <= BENCH.HEAVY_UNDER_MS ? 'heavy' : 'full');
  // Only a saved pick changes anything: preloadModel builds heavy on the next
  // camera start. Without storage it stays full and is measured next visit.
  try { localStorage.setItem(MODEL_KEY, JSON.stringify({ pick, fullMs: +median.toFixed(1), handsMs: +handsMedian.toFixed(1), path, at: Date.now() })); } catch { /* no storage */ }
}

/**
 * The models (pose nine megabytes, hands eight, and the engine) took thirty
 * seconds to arrive on a slow link, all of it spent after the player tapped
 * Start. So they are fetched and built the moment the Train screen opens,
 * while the player is still propping the phone up, inside the tracking worker
 * when this browser can run one (public/js/trackclient.js), on the page when it
 * cannot. The bytes are kept in the Cache API (trackengine.js modelBytes), so
 * only the first session downloads them. Built once and kept; a second session
 * reuses them.
 */
let trackerReady = null;
function preloadModel() {
  // A heavy pick from benchModel lands here, between sessions, never under a
  // running one.
  if (trackerReady && builtModel && builtModel !== POSE_MODEL() && !S.running) { tracker?.close(); tracker = null; trackerReady = null; }
  trackerReady ??= (async () => {
    const model = POSE_MODEL();
    builtModel = model;
    builtDelegate = pickDelegate();
    if (probe) probe.delegate = `${builtDelegate}${softwareGraphics() ? ' (software graphics)' : ''}`;
    const t = createTrackClient({
      visionUrl: VISION_URL, loadVision, model, delegate: builtDelegate,
      // Four bodies: a run has more than one in frame, and the player is picked
      // from them (pickPlayer). Hands: all 21 points of each hand, drawn over
      // the video; nothing is scored from them.
      numPoses: 4, hands: true,
      onResult: onTrackResult,
      // Where the models run decides how the hands fit around the pose when a
      // frame is too short for both (trackmath.js createCadence).
      onPath: (p) => {
        if (probe) probe.path = p;
        cadence.setMode(p === 'worker' ? 'together' : 'apart');
        // The worker gave out mid session and the page could not run the
        // models either: say so, rather than a camera that silently stops.
        // A GPU that fails mid session (its context lost, frames erroring) gets the processor once.
        // For this visit only: a GPU that fails once (a reset driver, a lost context) is not judged slow.
        if (!p && builtDelegate === 'GPU' && !DELEGATE_OVERRIDE) { failedOver = true; switchDelegate('CPU'); return; }
        if (!p && S.running) toast('Tracking stopped. End the session and start again.', true);
      },
    });
    tracker = t;
    probe?.bootFrom(() => t.timings);
    await t.ready;
    return t;
  })().catch((e) => {
    trackerReady = null; tracker = null;
    // Heavy chosen on this device and it would not load: full is already
    // cached from the benchmark, so this session runs on that.
    if (builtModel === 'heavy' && !MODEL_OVERRIDE && !heavyFailed) { heavyFailed = true; console.warn('heavy pose model failed, using full', e?.message); return preloadModel(); }
    throw e;
  });
  return trackerReady;
}

/**
 * What the camera is asked for. 1080p: the far player crop (lockcrop.js) and
 * the hand crops (trackmath.js) cut their squares out of the camera's own
 * pixels, and a far hand needs every one of them. Measured on 2026-09-25 with
 * scripts/perf-track.mjs, a player at half the usual size: with a wrist in
 * view, the hand model found a hand on 83% of its runs at 1080p and 52% at
 * 720p; at the usual distance the two were the same (84% and 81%) at the same
 * 29 tracked frames a second. The models themselves never see the whole frame
 * at that size; they scale it down on the GPU, in the tracking worker.
 */
// Up to 4K where the camera has it (most phone back cameras): the far search
// and the player crop cut the camera's own pixels, so more pixels is more
// distance. A laptop camera that tops out at 1080p or 720p simply gives that.
//
// 1080p to start, on every device (2026-10-04): a 4K stream on a phone cost a 4K picture handed to
// the tracker every frame, plus a 4K recording, for nothing while the player stands close. The camera
// steps up to 4K (CAMERA_FAR) only once the player is small in the frame and the GPU path has room
// for it (stepUpWhenFar); a camera that has no 4K simply stays where it is.
// resizeMode 'none': the camera's own frame at its nearest size, never cut to the asked shape. Chrome
// otherwise crops to fit: 1920x1080 asked of a camera giving 1080x1920 (a phone held upright) came back
// as a 1080x1080 middle with the head and feet cut off (2026-10-04). Browsers without it ignore it.
const CAMERA = (facing) => ({ width: { ideal: 1920 }, height: { ideal: 1080 }, resizeMode: 'none', facingMode: facing, frameRate: { ideal: 30 } });
const CAMERA_FAR = { width: { ideal: 3840 }, height: { ideal: 2160 }, resizeMode: 'none', frameRate: { ideal: 30 } };
const FAR_FRAMES = 20, FAR_SHOULDERS = 0.09, FAR_POSE_MS = 25;
let farCount = 0, steppedUp = false;
function stepUpWhenFar(lm) {
  if (steppedUp || !S.running || builtDelegate !== 'GPU' || !lm) return;
  farCount = lockcrop.shoulderWidth(lm) < FAR_SHOULDERS ? farCount + 1 : 0;
  if (farCount < FAR_FRAMES || !(cadence.poseMs < FAR_POSE_MS)) return;
  const track = $('cam').srcObject?.getVideoTracks?.()[0];
  const most = track?.getCapabilities?.().width?.max || 0;
  steppedUp = true;
  if (!track || most < 2560 || Math.max($('cam').videoWidth, $('cam').videoHeight) >= most) return;
  track.applyConstraints(CAMERA_FAR).then(() => console.info('camera: stepped up for a far player')).catch((e) => console.warn('camera step up', e?.message));
}

const LOAD_GIVE_UP_MS = 120000;
async function startCamera() {
  setLive(true);   // inside the tap: full screen is only granted to a gesture
  try {
    $('btnStart').disabled = true;
    $('btnStart').textContent = 'Loading tracking…';
    // Never a silent wait: say so at 20 s, give up at LOAD_GIVE_UP_MS with a reason and the button back.
    const slow = setTimeout(() => { $('btnStart').textContent = 'Still loading…'; $('gate').querySelector('p').textContent = 'Still loading the tracking. The first time can take up to a minute on a slow connection.'; }, 20000);
    try {
      await Promise.race([preloadModel(), new Promise((_, no) => setTimeout(() => no(new Error('the tracking took too long to load. Check the connection and tap Start again')), LOAD_GIVE_UP_MS))]);
    } catch (e) { try { tracker?.close(); } catch {} tracker = null; trackerReady = null; throw e; }
    finally { clearTimeout(slow); }
    const stream = await navigator.mediaDevices.getUserMedia({ video: CAMERA(S.facing) });
    $('cam').srcObject = stream;
    await $('cam').play();
    setStageShape();
    document.querySelector('.stage').classList.toggle('rear', S.facing === 'environment');
    $('btnFlip').hidden = !(navigator.mediaDevices.enumerateDevices) ;
    if (DEBUG) {
      try {
        trace.video = { w: $('cam').videoWidth, h: $('cam').videoHeight, ua: navigator.userAgent };
        document.getElementById('debugHud').removeAttribute('hidden');
        document.getElementById('btnTrace').removeAttribute('hidden');
      } catch (e) { console.error('debug hud', e); }
    }
    await beginSession();
    resetPlayerTracking();
    detector.setFrameWidth($('cam').videoWidth);
    bodyCap = createBodyCapture({ aspect: ($('cam').videoWidth / $('cam').videoHeight) || 16 / 9, height: $('cam').videoHeight || 1080 });
    S.running = true;
    agent?.context({ training: true, view: 'train' });
    // The movement layer counts per session, as game mode already does: each
    // session starts from nothing, and stopCamera flushes a rep still held.
    try { posemetrics.start?.(); } catch (e) { console.warn('pose panel', e); }
    restReset();
    startLoop();
    startDrawing();
    if (S.settings.recordSessions && 'MediaRecorder' in window) {
      try { recorder = vault.startRecording($('cam'), $('overlay'), { region: () => geom, maxSide: onPhone() ? 1280 : 1920 }); $('recBadge').hidden = false; }
      catch (e) { console.warn('recording unavailable', e); }
    }
  } catch (err) {
    setLive(false);
    console.error('startCamera', err);
    $('btnStart').disabled = false;
    $('btnStart').textContent = 'Start camera';
    toast(err.name === 'NotAllowedError' ? 'Camera permission denied.' : `Could not start: ${err.message}`, true);
  }
}

async function beginSession() {
  const s = await api('/api/sessions', { method: 'POST' });
  S.sessionId = s.id; S.shots = []; S.pendingIndex = null;
  $('gate').classList.add('hide');
  $('hud').hidden = false;
  $('rimHint').hidden = Boolean(S.rim);
  $('btnAsk').disabled = false;
  $('btnEnd').disabled = false;
  transcribe('sys', 'Session started.');
  const c = companionById(S.settings.companion);
  post(`Alright, I'm watching. Let's see it.`, 'coach', c.name);
}

// All 33 MediaPipe pose landmarks: face (0-10), arms + finger tips (11-22),
// legs, heels and toes (23-32). Scoring still reads shoulders/elbows/wrists/nose.
const BONES = [
  [11,12],[11,23],[12,24],[23,24],                       // torso
  [11,13],[13,15],[12,14],[14,16],                       // arms
  [15,17],[15,19],[15,21],[17,19],[16,18],[16,20],[16,22],[18,20], // hands: pinky, index, thumb
  [23,25],[25,27],[24,26],[26,28],                       // legs
  [27,29],[27,31],[29,31],[28,30],[28,32],[30,32],       // feet: heel + toe
  [0,1],[1,2],[2,3],[3,7],[0,4],[4,5],[5,6],[6,8],[9,10], // face: eyes, ears, mouth
];
const JOINTS = Array.from({ length: 33 }, (_, i) => i);

/**
 * Where the camera picture sits on the stage, in screen pixels, with the overlay canvas sized to
 * match: the stage's size times the screen's pixel density (capped), not the camera's. A 4K camera
 * shrunk onto a phone drew bones 0.6 screen pixels wide, which is nothing (2026-10-04). The picture is
 * shown whole (object-fit: contain), so a landmark goes at ox + x * pw, oy + y * ph, and lines are
 * given in screen pixels so they look the same on every device. Never sized to nothing: a 0 by 0
 * overlay is what once broke the next session's recording.
 */
const OVERLAY_MAX_PX = 2048;
/**
 * Phone training looks like TikTok Live (app.css :root.live): the camera fills the screen, full
 * screen where the browser allows it (Android Chrome does; an iPhone keeps its bars and the page fills
 * the rest), the page header and tabs go, the actions run down the right edge. Asked for inside the
 * Start tap, which full screen needs; undone when the session ends or the start fails.
 */
function setLive(on) {
  // Phones only: an iPad or a narrow laptop window keeps the page with its side panel.
  const want = Boolean(on) && onPhone() && Math.min(innerWidth, innerHeight) < 600;
  document.documentElement.classList.toggle('live', want);
  try {
    if (want && !document.fullscreenElement && document.documentElement.requestFullscreen) document.documentElement.requestFullscreen({ navigationUI: 'hide' }).catch(() => {});
    if (!want && document.fullscreenElement) document.exitFullscreen().catch(() => {});
  } catch { /* no full screen here: the page fills the screen itself */ }
}
let geom = null;
function overlayGeom() {
  const canvas = $('overlay'), video = $('cam');
  const w = canvas.clientWidth, h = canvas.clientHeight;
  if (!w || !h) return geom;
  const k = Math.min(window.devicePixelRatio || 1, 3, OVERLAY_MAX_PX / Math.max(w, h));
  const cw = Math.round(w * k), ch = Math.round(h * k);
  if (canvas.width !== cw || canvas.height !== ch) { canvas.width = cw; canvas.height = ch; }
  const vw = video.videoWidth || w, vh = video.videoHeight || h;
  // Always the whole picture (contain). In phone live mode it sits at the top of the screen: an upright
  // camera (9:16) on a taller phone (about 9:20) leaves a strip at the bottom, and the talk box and the
  // comments go there, clear of the player's feet. Filling the screen instead (cover) put the feet
  // behind the talk box (2026-10-04).
  const top = document.documentElement.classList.contains('live');
  const pos = top ? 'center top' : '';
  if (video.style.objectPosition !== pos) video.style.objectPosition = pos;
  const sc = Math.min(w / vw, h / vh);
  geom = { k, w, h, pw: vw * sc, ph: vh * sc, ox: (w - vw * sc) / 2, oy: top ? 0 : (h - vh * sc) / 2 };
  return geom;
}
/** The stage takes the camera's shape (app.css .stage): set when the stream starts or changes size. */
function setStageShape() {
  const v = $('cam');
  if (v.videoWidth && v.videoHeight) document.querySelector('.stage').style.setProperty('--cam-ar', `${v.videoWidth} / ${v.videoHeight}`);
}

const BONE_PX = 3.5, ARM_PX = 6, HAND_PX = 2.5;   // screen pixels
let lastWrist = null; // where the last shot released, for the element burst
let overlayFrames = 0;
function draw(lm, handList = []) {
  const canvas = $('overlay');
  const G = overlayGeom();
  if (!G) return;
  const ctx = canvas.getContext('2d');
  ctx.setTransform(1, 0, 0, 1, 0, 0);
  ctx.clearRect(0, 0, canvas.width, canvas.height);
  ctx.setTransform(G.k, 0, 0, G.k, 0, 0);
  elements.tick(ctx, G.w, G.h);
  const X = (p) => G.ox + (1 - p.x) * G.pw, Y = (p) => G.oy + p.y * G.ph;
  if (S.rim) {
    ctx.beginPath(); ctx.arc(X(S.rim), Y(S.rim), 14, 0, Math.PI * 2);
    ctx.lineWidth = 3; ctx.strokeStyle = '#ff5a1f'; ctx.stroke();
  }
  if (!lm) return;
  const fw = detector.last;
  if (fw && !fw.skip) { const w = lm[fw.side === 'left' ? 15 : 16]; if (w) lastWrist = { x: X(w), y: Y(w) }; }
  const armed = detector.state !== 'idle';
  const accent = armed ? '#ff5a1f' : '#2ea8ff';
  ctx.lineCap = 'round';
  for (const [a, b] of BONES) {
    const p = lm[a], q = lm[b];
    if (!p || !q) continue;
    const isArm = a >= 11 && a <= 16 && b >= 11 && b <= 16;
    ctx.strokeStyle = isArm && armed ? accent : 'rgba(255,255,255,.5)';
    ctx.lineWidth = isArm && armed ? ARM_PX : BONE_PX;
    ctx.shadowColor = isArm && armed ? accent : 'transparent';
    ctx.shadowBlur = isArm && armed ? 16 : 0;
    ctx.beginPath(); ctx.moveTo(X(p), Y(p)); ctx.lineTo(X(q), Y(q)); ctx.stroke();
  }
  ctx.shadowBlur = 0;
  // Elemental shot meter: live arc at the shooting wrist while the arm rises.
  const f = detector.last;
  if (f && !f.skip && armed) {
    const wi = f.side === 'left' ? 15 : 16;
    const w = lm[wi];
    if (w) elements.meter(ctx, X(w) + (f.side === 'left' ? 70 : -70), Y(w) - 10, f.progress, true);
  }
  for (const i of JOINTS) {
    const p = lm[i]; if (!p || (p.visibility !== undefined && p.visibility < 0.3)) continue;
    const key = i >= 13 && i <= 16;
    const tip = (i >= 17 && i <= 22) || i >= 29 || i <= 10; // fingers, feet, face
    ctx.fillStyle = key ? accent : tip ? '#22d97a' : '#fff';
    ctx.beginPath(); ctx.arc(X(p), Y(p), key ? 7 : tip ? 3 : 4.5, 0, Math.PI * 2); ctx.fill();
  }
  if (handList.length) drawHands(ctx, handList, X, Y);
  // ?perf only: what was drawn and where on screen, for scripts/mobile-check.mjs.
  if (probe) {
    const r = canvas.getBoundingClientRect(), rear = S.facing === 'environment';
    const at = (p) => (p && (p.visibility ?? 1) >= 0.5 ? { x: r.left + (rear ? G.w - X(p) : X(p)), y: r.top + Y(p) } : null);
    window.__hbOverlay = { delegate: builtDelegate, path: tracker?.path || null, frames: ++overlayFrames, boneCssPx: BONE_PX, handCssPx: HAND_PX, handsDrawn: handList.length, nose: at(lm[0]), ankles: [at(lm[27]), at(lm[28])].filter(Boolean), ankleVis: [lm[27]?.visibility, lm[28]?.visibility].map((x) => (x == null ? null : +x.toFixed(2))), ankleY: [lm[27]?.y, lm[28]?.y].map((x) => (x == null ? null : +x.toFixed(2))) };
  }
}

// 21 hand landmarks per hand: wrist, then four joints per finger, tip last.
const HAND_BONES = [[0,1],[1,2],[2,3],[3,4],[0,5],[5,6],[6,7],[7,8],[5,9],[9,10],[10,11],[11,12],[9,13],[13,14],[14,15],[15,16],[13,17],[17,18],[18,19],[19,20],[0,17]];
const FINGER_TIPS = [4, 8, 12, 16, 20];
function drawHands(ctx, handList, X, Y) {
  ctx.lineCap = 'round'; ctx.lineWidth = HAND_PX; ctx.strokeStyle = 'rgba(34,217,122,.85)';
  for (const { lm: h } of handList) {
    for (const [a, b] of HAND_BONES) {
      ctx.beginPath(); ctx.moveTo(X(h[a]), Y(h[a])); ctx.lineTo(X(h[b]), Y(h[b])); ctx.stroke();
    }
    for (let i = 0; i < h.length; i++) {
      const tip = FINGER_TIPS.includes(i);
      ctx.fillStyle = tip ? '#ffbe6b' : '#22d97a';
      ctx.beginPath(); ctx.arc(X(h[i]), Y(h[i]), tip ? 5 : 2.5, 0, Math.PI * 2); ctx.fill();
    }
  }
}

/* ------------------------------------------------------ which body is YOU
 * The pose model is asked for up to four bodies, because people walk past. It
 * hands them back in no particular order, and this loop used to take the first
 * one as the player. So a friend strolling through the back of the picture
 * could become "the player" for a few frames: the skeleton jumped onto them,
 * and the shot detector was fed half of one body's motion and half of
 * another's. That is what inconsistent tracking looked like from the outside.
 *
 * Now every body goes through the tracker, which keeps each one in its own
 * slot, and the player is a slot, not an array position. The player is whoever
 * has been in the picture longest: they set the camera down and stepped back,
 * so they were there first. Once chosen they stay chosen, and a passer by never
 * takes over. Only if the player is gone for a good while does it look again,
 * unless they are plainly alone: the rules are createPlayerLock in scoring.js.
 *
 * The chosen body's points are then steadied before they are drawn or scored.
 */
const soloTracker = createPoseTracker();
const soloSmoother = createLandmarkSmoother();
const playerLock = createPlayerLock();

let lastPlayerLm = null, farWarnedAt = 0;
/**
 * Which parts of the locked player the camera has right now, for the companion.
 * Sent with every question during a session so "can you see my shoulders"
 * is answered from the landmarks and not made up.
 */
const PART_POINTS = { head: [0], shoulders: [11, 12], elbows: [13, 14], wrists: [15, 16], hips: [23, 24], knees: [25, 26], feet: [27, 28] };
function cameraSees() {
  const lm = lastPlayerLm;
  if (!lm) return { body: false };
  const out = { body: true, far: detector.last?.skip === 'too-far' };
  for (const [part, idx] of Object.entries(PART_POINTS)) {
    out[part] = idx.every((i) => (lm[i]?.visibility ?? 0) >= 0.5 && lm[i].x > 0 && lm[i].x < 1 && lm[i].y > 0 && lm[i].y < 1);
  }
  return out;
}

function resetPlayerTracking() {
  soloTracker.reset(); soloSmoother.reset(); lockcrop.reset(); handTrack.reset(); cadence.reset();
  playerLock.reset(); lastPlayerLm = null; lastSmoothed = null; lastPoseCount = 0; farCount = 0; steppedUp = false;
  Object.assign(trackHealth, { since: 0, times: [], slowSaid: false, handsSaid: false });
  bodyCap?.reset({ aspect: $('cam').videoWidth / $('cam').videoHeight, height: $('cam').videoHeight });
}

/** Tell them once in a while, in words, when they are too small to score. */
function tooFarHint(now) {
  if (detector.last?.skip !== 'too-far' || now - farWarnedAt < 40000) return;
  farWarnedAt = now;
  post('You are too far for me to score your form. Step in a bit, or move the camera out toward you.', 'warn', companionById(S.settings.companion).name);
}

function pickPlayer(people, now) {
  const { me, switched } = playerLock.pick(people, soloTracker.slots, now);
  if (switched) {
    soloSmoother.reset();   // never blend one body's points into another's
    handTrack.reset();
    // Nor finish one body's shot on another body's arm. A shot half read when
    // the player changes is dropped, and the new body has to show its arm down
    // before a shot can start. On the driveway clip the model read a girl and
    // her father's raised arm as one body; the arm went up on one tracker slot
    // and "came down" on the next, and that was counted as a shot on every pass
    // of the recorded clip until this (2026-09-25).
    if (detector.state !== 'idle') detector.reset();
  }
  return me;
}

/* ------------------------------------------------------------ the frame loop
 * Every new camera frame is handed to the tracker (trackclient.js), which reads
 * it in the tracking worker, off this thread, and answers with every landmark
 * in whole frame units. This thread never waits on a model, so the camera, the
 * buttons and her voice stay smooth however slow the phone's GPU is.
 *
 * Nothing queues. A frame that arrives while the last one is still being read
 * is dropped, and the newest frame is sent the moment the answer is back, so
 * the overlay is always about now, never about a second ago.
 * requestVideoFrameCallback fires once per camera frame where the browser has
 * it; elsewhere a paint callback checks whether the picture changed.
 *
 * What runs on each frame is the cadence's call (trackmath.js createCadence):
 * pose and hands together while they fit in a frame. When they do not, in the
 * worker the pose still runs on every frame and the hands ride along on some
 * of them; on the page the hands take turns with the pose instead, so no
 * single frame holds this thread for both.
 */
const handTrack = createHandTracker();
const cadence = createCadence();
const LOOKS_EVERY = 3;           // shirt colours, on one full frame in three, only with more than one body in view
let loopGen = 0, waiting = false, lastSmoothed = null, lastPoseCount = 0, frameNo = 0, sendMs = 0;
const camTimes = [];
const hasRvfc = typeof HTMLVideoElement !== 'undefined' && 'requestVideoFrameCallback' in HTMLVideoElement.prototype;

const hipOf = (lm) => (lm?.[23] && lm?.[24] ? { x: (lm[23].x + lm[24].x) / 2, y: (lm[23].y + lm[24].y) / 2 } : null);
const wristsOf = (lm) => (lm ? {
  left: (lm[15]?.visibility ?? 1) >= 0.3 ? lm[15] : null,
  right: (lm[16]?.visibility ?? 1) >= 0.3 ? lm[16] : null,
} : null);

function startLoop() {
  const gen = ++loopGen;
  const video = $('cam');
  let usePaint = !hasRvfc, lastStep = performance.now();
  const live = () => gen === loopGen && S.running;
  const step = () => { if (!live()) return; lastStep = performance.now(); takeFrame(video); schedule(); };
  const onFrame = () => { if (!usePaint) step(); };
  const onPaint = () => { if (usePaint) step(); };
  const schedule = () => { if (live()) usePaint ? requestAnimationFrame(onPaint) : video.requestVideoFrameCallback(onFrame); };
  // Frame callbacks that stop coming while the video plays (a browser that
  // drops them after a stream change) must not stop tracking: the loop then
  // checks for a new picture on every paint instead, as it used to.
  const guard = setInterval(() => {
    if (!live()) { clearInterval(guard); return; }
    if (!usePaint && !video.paused && performance.now() - lastStep > 1500) { usePaint = true; schedule(); }
  }, 1000);
  schedule();
}

/** A new picture may be on the video. Send it, or note that one is waiting. */
function takeFrame(video) {
  if (video.readyState < 2 || video.currentTime === lastVideoTime) return;
  // The camera's real frame interval is the time budget the cadence plans in.
  const now = performance.now();
  camTimes.push(now); if (camTimes.length > 31) camTimes.shift();
  if (camTimes.length > 10) cadence.setBudget((now - camTimes[0]) / (camTimes.length - 1));
  if (!tracker || tracker.busy || !tracker.path) { waiting = true; probe?.dropped(); return; }
  sendFrame(video);
}

function sendFrame(video) {
  const t0 = performance.now();
  waiting = false;
  lastVideoTime = video.currentTime;
  const plan = cadence.next({ shooting: detector.state !== 'idle', hands: tracker.hasHands });
  // Small on screen means the pose model looks through a box around the
  // player instead of at the whole frame. See lockcrop.js.
  const box = plan.pose ? lockcrop.plan(lastPlayerLm) : null;
  frameNo++;
  const looks = plan.pose && !box && lastPoseCount > 1 && frameNo % LOOKS_EVERY === 0;
  // On the page path the answer is handled inside submit(), so the time so far
  // is recorded first; on the worker path the answer comes later and the cost
  // of handing the frame over is added to it.
  sendMs = performance.now() - t0;
  const t1 = performance.now();
  const maxSide = MAXSIDE_OVERRIDE || (builtDelegate === 'CPU' ? CPU_SIDE : 0);
  const sent = tracker.submit(video, t1, { pose: plan.pose, hands: plan.hands, box, hint: hipOf(lastPlayerLm), looks, maxSide });
  if (tracker.path === 'worker') sendMs += performance.now() - t1;
  if (sent) probe?.captured();
}

/**
 * A wrist the pose model barely sees, from the hand the hand model found at it
 * on this very frame (never a held hand). Far from the camera the pose model's
 * wrist confidence falls first: measured 2026-09-30 on a player at a sixth of a
 * 1440p frame, "no wrist" skips were 75 of about 150 frames against 39 at half
 * that distance. The hand model reads the same wrist from a zoomed crop
 * (trackmath.js), so its point stands in, and only when the pose's is unseen.
 */
function wristFromHands(lm, hands) {
  if (!lm || !hands?.length) return lm;
  let out = lm;
  for (const h of hands) {
    const i = h.side === 'left' ? 15 : h.side === 'right' ? 16 : -1;
    const w = h.lm?.[0];
    if (i < 0 || !w || !lm[i] || (lm[i].visibility ?? 1) >= TUNABLES.MIN_VISIBILITY) continue;
    if (out === lm) out = Object.assign(lm.slice(), { world: lm.world });
    out[i] = { ...lm[i], x: w.x, y: w.y, visibility: 0.9 };
  }
  return out;
}

/* ---------------------------------------------------- drawing between results
 * The overlay used to be drawn only when a result came back, so it always showed the body as it
 * was one model-time ago, and nothing moved between results. Now it is drawn on every screen
 * refresh from the latest result, each body point moved on by its own speed (from the last two
 * results) for the time since its frame was taken, capped. Hands are drawn as read. Scoring and
 * the shot detector never see these guesses: only the drawing does.
 */
// Hands get no lead: fingers turn and stop too fast for a guess, and the overshoot read as hands
// that did not track properly (2026-10-10). The body gets a short one.
const LEAD_MAX_MS = 40, LEAD_MAX_STEP = 1, LEAD_MAX_MOVE = 0.04;
const view = { lm: null, lmPrev: null, t: 0, tPrev: 0, hands: [] };
let drawGen = 0;
function ahead(cur, prev, dt, lead) {
  if (!cur || !prev || !(dt > 0) || !(lead > 0)) return cur;
  const f = Math.min(lead / dt, LEAD_MAX_STEP);
  return cur.map((p, i) => {
    const q = prev[i];
    if (!p || !q) return p;
    let dx = (p.x - q.x) * f, dy = (p.y - q.y) * f;
    const m = Math.hypot(dx, dy);
    if (m > LEAD_MAX_MOVE) { dx *= LEAD_MAX_MOVE / m; dy *= LEAD_MAX_MOVE / m; }
    return { ...p, x: p.x + dx, y: p.y + dy };
  });
}
function noteView(lm, hands, t) {
  if (lm !== view.lm) { view.lmPrev = view.lm; view.tPrev = view.t; view.lm = lm; view.t = t; }
  view.hands = hands;
}
function startDrawing() {
  const gen = ++drawGen;
  const frame = () => {
    if (gen !== drawGen || !S.running) return;
    const now = performance.now();
    const lead = Math.min(LEAD_MAX_MS, now - view.t);
    const lm = view.lm && ahead(view.lm, view.lmPrev, view.t - view.tPrev, lead);
    draw(lm, view.hands);
    requestAnimationFrame(frame);
  };
  requestAnimationFrame(frame);
}

/** One answer from the tracker: the player, their hands, the shot detector, the overlay. */
/**
 * ?perf: the numbers on screen, so a player's own phone or laptop can be read from a screenshot
 * (frames a second, where the models run, camera size, model times, how often the hands run).
 * Nothing is sent anywhere.
 */
let perfEl = null, perfAt = 0;
function perfPanel() {
  if (!probe) return;
  const now = performance.now();
  if (now - perfAt < 1000) return;
  perfAt = now;
  if (!perfEl) { perfEl = Object.assign(document.createElement('pre'), { className: 'perfpanel' }); document.body.append(perfEl); }
  const s = probe.snapshot(), sec = Math.max(1, s.windowMs / 1000);
  const med = (a) => { const v = a.filter(Number.isFinite).slice(-60).sort((x, y) => x - y); return v.length ? Math.round(v[Math.floor(v.length / 2)]) : '-'; };
  const v = $('cam');
  softwareGraphics();
  perfEl.textContent = [
    `fps ${(s.frames / sec).toFixed(1)}  hands/s ${(s.handRuns / sec).toFixed(1)}`,
    `pose ${med(s.poseMs)} ms  hands ${med(s.handsMs)} ms  lag ${med(s.lat)} ms`,
    `${builtDelegate || '?'} in ${tracker?.path || '-'}  camera ${v.videoWidth}x${v.videoHeight}`,
    `gpu ${gpuName.slice(0, 40) || '?'}`,
  ].join('\n');
}

const trackHealth = { since: 0, times: [], slowSaid: false, handsSaid: false };
/** Tell the player, once a session, when tracking is slow here or the hands are off, never leave them guessing. */
function noteHealth(now) {
  if (!trackHealth.since) trackHealth.since = now;
  trackHealth.times.push(now); while (trackHealth.times.length && now - trackHealth.times[0] > 5000) trackHealth.times.shift();
  if (now - trackHealth.since < 15000 || !delegateDone) return;
  const fps = trackHealth.times.length / 5;
  const who = companionById(S.settings.companion).name;
  if (!trackHealth.slowSaid && fps < 6) { trackHealth.slowSaid = true; post('Tracking is running slowly on this device. Closing other apps and tabs helps, and so does a phone that is not hot.', 'warn', who); }
  if (!trackHealth.handsSaid && tracker && !tracker.hasHands && now - trackHealth.since > 20000) { trackHealth.handsSaid = true; post('Hand tracking is off on this device because it is running slowly. Your body is still tracked and scored.', 'warn', who); }
}

function onTrackResult(r) {
  if (!S.running) return;             // an answer that landed after the session ended
  const t0 = performance.now();
  noteHealth(t0);
  perfPanel();
  const now = r.t;                    // when the frame was taken, not when the answer came
  cadence.note({ pose: r.ms?.pose ?? null, hands: r.ms?.hands ?? null });
  probe?.step({ ...r.ms, crops: r.handCrops ?? null, capture: r.captureMs ?? null, bodies: r.poses?.length ?? null, found: r.hands ? r.hands.length : null, t: r.t });
  let raw = lastPlayerLm, lm = lastSmoothed, shot = null;
  if (r.poses) {
    benchModel(r.ms?.pose, r.ms?.hands, r.path);
    benchDelegate(r.ms?.pose, Number.isFinite(r.ms?.hands));
    const poses = r.poses;
    lastPoseCount = poses.length;
    // The world points ride on each pose as lm.world: metres about the hips,
    // so a crop needs no remapping. scoring.js reads them (worldOf).
    poses.forEach((p, i) => { if (r.worlds?.[i]) p.world = r.worlds[i]; });
    const me = pickPlayer(soloTracker.push(poses, now, { looks: r.looks || null }).people, now);
    raw = me ? me.lm : null;
    lastPlayerLm = raw;
    stepUpWhenFar(raw);
    // A body the far search (lockcrop.js tiles) found is followed while the
    // tracker decides it is the player, so the zoom does not wander off it.
    if (raw) lockcrop.follow(raw);
    else if (r.cropped && poses.length) lockcrop.follow(poses.reduce((a, b) => (lockcrop.shoulderWidth(b) > lockcrop.shoulderWidth(a) ? b : a)));
    else if (r.lost) lockcrop.reset();
    lm = raw ? soloSmoother.push(raw, now) : null;
    lastSmoothed = lm;
    // Body Map: the smoothed player (lm.world rides along) and only the hands
    // the model found on this very frame, never the held ones handTrack carries.
    if (lm) bodyCap?.push(now, lm, r.hands);
  }
  // Hands: fresh from this frame when the hand model ran on it, otherwise the
  // last ones, held a moment and carried with the wrist (handtrack.js).
  const shown = handTrack.push(r.hands ?? null, wristsOf(lm), now);
  noteView(lm, shown, now);
  const fresh = r.hands ? r.hands.length : null;
  if (r.poses) {
    tooFarHint(now);
    // The same landmarks feed the movement detectors: dribble, balance, stance,
    // box out, drive and the fatigue drift across the session.
    try { posemetrics.onFrame?.(raw, now, r.poses); } catch (e) { /* never break the loop */ }
    shot = detector.push(wristFromHands(lm, r.hands), now);
    probe?.skip(detector.last?.skip);
    if (DEBUG) { try { recordFrame(now); if (shot) trace.shots.push({ t: Math.round(now), ...shot }); } catch (e) { console.error('trace', e); } }
    if (shot) onShot(shot);
    // Main thread time for this frame: sending it, the models when they ran on
    // the page, and everything above.
    probe?.frame({ ms: sendMs + (r.path === 'main' ? (r.ms?.total || 0) : 0) + performance.now() - t0,
      lm: raw, hands: shown.length, fresh, shot: Boolean(shot), cropped: r.cropped, lat: performance.now() - r.t, poseMs: r.ms?.pose ?? null, handsMs: r.ms?.hands ?? null, bodies: r.poses.length });
  } else probe?.handsOnly({ ms: sendMs + (r.path === 'main' ? (r.ms?.total || 0) : 0) + performance.now() - t0, lm: raw, fresh: fresh ?? 0, handsMs: r.ms?.hands ?? null });
  // A frame came in while this one was being read: send the newest now rather
  // than at the next camera tick.
  if (waiting && tracker && !tracker.busy) {
    const v = $('cam');
    if (v.readyState >= 2 && v.currentTime !== lastVideoTime) sendFrame(v);
  }
}

/* ------------------------------------------------------------ rim and zones
 * One tap on the preview marks the rim; the arithmetic lives in scoring.js
 * (`rimDistance`) so it can be tested without a browser. Every distance the
 * app shows from it is labelled a camera estimate.
 */
const ZONE_LABEL = { rim: 'at the rim', paint: 'in the paint', mid: 'mid range', 'long-two': 'long two', three: 'three' };

/** Where the tap landed, in frame units. The video is mirrored; the mark is stored unmirrored. */
function markRim(ev) {
  const cv = $('overlay'); const G = geom || overlayGeom(); if (!G) return;
  const r = cv.getBoundingClientRect();
  // Where on the picture (shown whole, overlayGeom), in frame units.
  const fx = (ev.clientX - r.left - G.ox) / G.pw, fy = (ev.clientY - r.top - G.oy) / G.ph;
  if (fx < 0 || fy < 0 || fx > 1 || fy > 1) return;
  S.rim = { x: S.facing === 'environment' ? fx : 1 - fx, y: fy };
  $('rimHint').hidden = true;
  toast('Rim marked. Distances from here on are camera estimates.');
}

/** Distance and zone for this shot, from the player's feet to the rim mark. */
function shotDistance(lm) {
  const v = $('cam');
  return rimDistance(lm, S.rim, v.videoWidth || 1280, v.videoHeight || 720);
}

async function onShot(shot) {
  // The free demo is five shots: anything after the fifth, while it winds down, is not counted.
  // Counted as each shot lands, not after it is saved, so two quick shots cannot both slip in.
  if (demoMode) { if (demoTaken >= DEMO_SHOTS) return; demoTaken++; }
  // Body Map: before any await, because the capture reads the buffer now.
  const bodyP = bodyCap?.onShot(shot, { rim: S.rim, placement: bodymap.placement() }) ?? null;
  const element = elementFor(shot.overall);
  shot.element = element.id;
  const where = shotDistance(lastPlayerLm);
  if (where) Object.assign(shot, where);
  // Eyes on the rim at release: only a call when the rim is marked and the
  // head was turned enough to read, and only ever "on" or "off".
  const eyes = eyesOnRim(shot.metrics, S.rim);
  if (eyes) shot.eyes = eyes;
  const at = lastWrist || { x: (geom?.w || 640) / 2, y: (geom?.h || 360) / 2 };
  elements.burst(element, at.x, at.y - 40, shot.overall);
  // After the session (the demo's last shot), nothing else draws: play the burst out here.
  if (!S.running) { const t0 = performance.now(); const anim = () => { if (S.running) return; draw(null); if (performance.now() - t0 < 1600) requestAnimationFrame(anim); }; requestAnimationFrame(anim); }
  const el = $('pop');
  el.innerHTML = `${elements.icon(element.id, 34)}<span>${shot.overall}</span>`;
  el.style.color = element.color;
  el.classList.remove('go'); void el.offsetWidth; el.classList.add('go');
  $('hElement').innerHTML = `${elements.icon(element.id, 16)} ${element.label}`;
  $('hElement').style.color = element.color;

  ['Elbow', 'Release', 'Follow'].forEach((n, i) => {
    const v = [shot.scores.elbow, shot.scores.release, shot.scores.follow][i];
    $(`m${n}`).textContent = v; $(`b${n}`).style.width = `${v}%`;
  });
  $('btnMake').disabled = false; $('btnMiss').disabled = false;

  try {
    const { shot: saved, moments } = await api(`/api/sessions/${S.sessionId}/shots`, {
      method: 'POST', body: JSON.stringify(shot),
    });
    S.shots.push(saved); S.pendingIndex = saved.index;
    const bodySid = S.sessionId;
    bodyP?.then((b) => { if (b) queueBody(bodySid, saved.index, b.snapshot); });
    restReset();
    if (saved.zone) post(`About ${saved.distanceFt} ft, ${ZONE_LABEL[saved.zone]}. Camera estimate.`, 'note');
    if (saved.eyes === 'off') post('Eyes were off the rim at release.', 'warn');
    moments.forEach((m, i) => {
      const chip = post(m.text, m.tone, companionById(S.settings.companion).name);
      if (i === 0 && chip) chip.insertAdjacentHTML('afterbegin', `<span class="el">${elements.icon(element.id, 16)}</span>`);
    });
    if (S.settings.autoNarrate && moments[0]) speak(moments[0].text, {
      kind: 'reaction',
      onStart: () => companionState('talking'),
      onEnd: () => companionState('idle'),
    });
    renderHud(); renderLog();
    // The free demo ends itself after the fifth shot, once her reaction has had a moment.
    if (demoMode) { paintDemo(); if (S.shots.length >= DEMO_SHOTS && !demoEnding) { demoEnding = true; setTimeout(() => endSession(), 2500); } }
    // Long-form coaching waits for "How did I do?" or the end-of-session report.
  } catch (err) {
    // A shot that was not saved was not taken, as far as the free demo goes:
    // counted anyway, the demo sat at 4 of 5 for good (seen 2026-09-25).
    if (demoMode && demoTaken > S.shots.length) demoTaken--;
    toast(err.message, true);
  }
}

function renderHud() {
  const s = summarize(S.shots);
  $('hAtt').textContent = s.attempts; $('hMakes').textContent = s.makes;
  $('hPct').textContent = `${s.pct}%`; $('hForm').textContent = s.attempts ? s.avg.overall : '\u2013';
  const se = sessionElement(S.shots);
  if (se) { $('hSession').innerHTML = `${elements.icon(se.id, 16)} ${se.label}`; $('hSession').style.color = se.color; }
}

function renderLog() {
  $('log').innerHTML = S.shots.length
    ? [...S.shots].reverse().map((s) =>
        `<span class="chip ${s.made === true ? 'made' : s.made === false ? 'missed' : ''}">${s.element ? elements.icon(s.element, 12) : ''} #${s.index} &middot; ${s.overall}</span>`).join('')
    : '<span style="color:var(--mute);font-size:13px">Nothing yet.</span>';
}

async function mark(made) {
  if (S.pendingIndex === null) return;
  const index = S.pendingIndex; S.pendingIndex = null;
  $('btnMake').disabled = true; $('btnMiss').disabled = true;
  try {
    await api(`/api/sessions/${S.sessionId}/shots/${index}`, { method: 'PATCH', body: JSON.stringify({ made }) });
    const shot = S.shots.find((s) => s.index === index);
    if (shot) shot.made = made;
    renderHud(); renderLog();
  } catch (err) { toast(err.message, true); }
}

async function askCoach(auto = false) {
  if (!S.shots.length) return toast('Put a few shots up first.');
  if (!auto) { $('btnAsk').disabled = true; $('btnAsk').textContent = 'Watching…'; }
  companionState('thinking');
  if (!auto) transcribe('you', 'How did I do?');
  const voice = speakStream({ onStart: () => companionState('talking'), onEnd: () => companionState('idle') });
  const line = transcribeStream('coach');
  let live = null;
  try {
    const { text } = await streamApi(`/api/sessions/${S.sessionId}/coach?stream=1`, { question: 'How did I do?' }, {
      onText: (t) => { voice.push(t); line.push(t); live = feedFirstSentence(live, t); },
    });
    voice.end();
    line.end(text);
  } catch (err) { voice.end(); line.end(''); toast(err.message, true); companionState('idle'); }
  finally { $('btnAsk').disabled = false; $('btnAsk').textContent = 'How did I do?'; }
}

async function stopRecording(sessionId) {
  $('recBadge').hidden = true;
  if (!recorder) return;
  const r = recorder; recorder = null;
  try {
    const { blob, duration, poster } = await r.stop();
    if (blob.size < 50_000) return; // nothing worth keeping
    const sum = summarize(S.shots);
    await vault.saveClip({ id: `c${Date.now()}`, sessionId, at: new Date().toISOString(), size: blob.size, duration, poster, blob,
      attempts: sum.attempts, makes: sum.makes, form: sum.avg.overall, companion: S.settings.companion });
    toast(`Recording saved to your Vault (${vault.fmtBytes(blob.size)}).`);
  } catch (e) { toast(`Could not save recording: ${e.message}`, true); }
}

/* --------------------------------------------------------------- breathers
 * A session is reps and the gaps between them. The reps get a caption each.
 * The gaps used to get silence, which reads as the companion having wandered
 * off. Now a long enough gap gets a line from the rest bank: a joke, always at
 * the ball's expense, never a nudge to get back to work. A rest is allowed to
 * be a rest.
 *
 * At most three per breather, spaced out, and the count resets on the next
 * shot. A companion that fills every silence is worse than one that fills none.
 */
const REST_FIRST_MS = 25000;      // how long before a gap counts as a breather
const REST_AGAIN_MS = 50000;      // and how long before it says something else
const REST_MAX = 3;
let restTimer = null, restSaid = 0;

function restWatch() {
  clearTimeout(restTimer);
  if (!S.running) return;
  if (restSaid >= REST_MAX) return;
  restTimer = setTimeout(sayRestLine, restSaid === 0 ? REST_FIRST_MS : REST_AGAIN_MS);
}

async function sayRestLine() {
  if (!S.running || !S.sessionId) return;
  // Not over the top of a conversation or a reply that is still playing.
  if (conversation.live || companionSpeaking()) { restWatch(); return; }
  try {
    const r = await api(`/api/sessions/${S.sessionId}/rest`, { method: 'POST' });
    if (!S.running) return;
    restSaid++;
    post(r.text, 'rest', companionById(S.settings.companion).name);
    if (S.settings.autoNarrate) speak(r.text, { onStart: () => companionState('talking'), onEnd: () => companionState('idle') });
  } catch { /* a missed joke is not an error worth showing */ }
  restWatch();
}

/** A shot happened: whatever breather was under way is over. */
function restReset() { restSaid = 0; restWatch(); }

/**
 * Swap between the front and the back camera without ending the session. The
 * front camera is a mirror; the back one is not, so the stage flips the video
 * and the overlay together and the rim mark is read the other way round.
 */
async function flipCamera() {
  if (!S.running) return;
  S.facing = S.facing === 'user' ? 'environment' : 'user';
  const cam = $('cam');
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ video: CAMERA(S.facing) });
    cam.srcObject?.getTracks().forEach((t) => t.stop());
    cam.srcObject = stream; await cam.play();
    detector.setFrameWidth(cam.videoWidth);   // the other camera may capture at another size
    document.querySelector('.stage').classList.toggle('rear', S.facing === 'environment');
    S.rim = null; $('rimHint').hidden = false; resetPlayerTracking();
    // A new stream on the same <video>: the frame callbacks are started again
    // on it, so tracking never waits on a callback the old stream owed.
    lastVideoTime = -1; startLoop();
    toast(S.facing === 'user' ? 'Front camera.' : 'Back camera. Tap the rim again.');
  } catch (e) { S.facing = S.facing === 'user' ? 'environment' : 'user'; toast(`Could not switch camera: ${e.message}`, true); }
}

function stopCamera() {
  setLive(false);
  S.rim = null; $('rimHint').hidden = true; $('btnFlip').hidden = true;
  S.running = false;
  agent?.context({ training: false });
  try { posemetrics.stop?.(); } catch (e) { console.warn('pose panel', e); }
  clearTimeout(restTimer); restSaid = 0;
  const cam = $('cam');
  cam.srcObject?.getTracks().forEach((t) => t.stop());
  cam.srcObject = null;
  detector.reset();
  // The frame loop stops on its own with S.running; the tracker and its models
  // stay built for the next session.
  loopGen++; drawGen++; waiting = false; handTrack.reset();
  Object.assign(view, { lm: null, lmPrev: null, hands: [] });
  draw(null);
}

async function endSession() {
  $('btnEnd').disabled = true; $('btnAsk').disabled = true;
  const endedId = S.sessionId;
  const saving = stopRecording(endedId);
  stopCamera();
  await saving;
  // Body Map: finish every shot still waiting for its landing and send what
  // waits, before the report, so the Worker has it when it settles the session.
  try { await bodyCap?.flush(); await flushAllBody(); } catch { /* never in the way of the report */ }
  const hadShots = S.shots.length > 0;
  if (hadShots) { $('btnEnd').textContent = 'Writing report…'; post('Session over. Writing your report…', 'note'); companionState('thinking'); }
  else { transcribe('sys', 'Session ended. No shots recorded.'); toast('Session ended. Camera off.'); }
  try {
    if (!hadShots) return;
    // The report route also settles the session into challenges and the ladder
    // and re-checks badges. Those are consumed server side whether or not we
    // read them, so a dropped justEarned is a badge the player never sees.
    const { report, justEarned, social } = await api(`/api/sessions/${endedId}/report`, { method: 'POST' });
    social?.challenges?.filter((c) => c.counted).forEach((c) => post(`Posted to "${c.title}".`, 'mark'));
    if (justEarned?.length) social_celebrate(justEarned);
    post(report, 'coach', null, true);
    transcribe('sys', 'Session ended and saved.');
    // Auto-talk (Live commentary in Settings) covers the report too. Each session ends with the
    // choice to switch it off, or, when it is off, to hear this one report anyway.
    const readReport = () => speakLong(report, { onStart: () => companionState('talking'), onEnd: () => companionState('idle') });
    if (S.settings.autoNarrate) {
      readReport();
      toastAction('Session saved. She is reading your report.', 'Turn off auto-talk', async () => {
        stopSpeaking(); companionState('idle');
        await patch({ autoNarrate: false });
        toast('Auto-talk is off. She still answers when you talk to her. Turn it back on in Settings, Live commentary.');
      });
    } else {
      toastAction('Session saved to your history.', 'Read it to me', readReport);
    }
    // Show the full record, then reset the stage for the next session. The
    // free demo stays on Train, where the offer is.
    if (!demoMode) await openSession(endedId);
  } catch (err) { toast(err.message, true); }
  finally {
    saveTrace();
    S.sessionId = null; S.shots = []; S.pendingIndex = null;
    $('btnEnd').textContent = 'End session'; $('btnEnd').disabled = true;
    $('btnMake').disabled = true; $('btnMiss').disabled = true;
    $('hud').hidden = true;
    $('gate').classList.remove('hide');
    const demoDone = demoMode && demoEnding;
    $('gate').querySelector('h2').textContent = demoDone ? 'That was your free 5-shot demo.' : 'Ready for another one?';
    $('demoCta').hidden = !demoDone;
    if (demoDone) {
      $('gate').querySelector('p').textContent = 'That is what she sees in five shots. Join the waitlist to keep every session, or keep shooting now.';
      demoMode = false; demoEnding = false; demoTaken = 0; $('demoBanner').hidden = true;
    }
    $('btnStart').disabled = false; $('btnStart').textContent = demoDone ? 'Keep shooting' : 'Start camera';
    renderLog();
  }
}

/** Demo path: pushes synthetic shots through the exact same pipeline. */
async function simulate() {
  if (!S.sessionId) await beginSession();
  $('btnSim').disabled = true; $('btnSim').textContent = 'Running…';
  const base = 88;
  for (let i = 0; i < 8; i++) {
    if (!S.sessionId) break;   // the session ended under it (the free demo ends itself at five)
    const drift = i * 3 + Math.random() * 6;
    const e = Math.max(40, Math.round(base - drift));
    const r = Math.max(35, Math.round(base - 12 - drift));
    const f = Math.max(42, Math.round(base + 2 - drift));
    await onShot({
      scores: { elbow: e, release: r, follow: f },
      overall: Math.round(e * 0.4 + r * 0.35 + f * 0.25),
      metrics: { elbowOffset: 0.1, releaseAngle: 60, followFrames: 10 },
      side: 'right', made: null,
    });
    await mark(Math.random() > 0.42);
    await new Promise((res) => setTimeout(res, 620));
  }
  $('btnSim').disabled = false; $('btnSim').textContent = 'Simulate 8 shots';
}

/* --------------------------------------------------------------- sessions */

async function renderSessions() {
  const list = await api('/api/sessions');
  $('slist').innerHTML = list.length ? list.map((s) => {
    const d = new Date(s.startedAt);
    return `<button class="sitem" data-id="${s.id}">
      <div class="ball">${companionById(s.companion).image ? `<img src="${companionById(s.companion).image}" width="44" height="44" alt="" style="display:block">` : companionSVG(s.companion, { size: 44, idle: false })}</div>
      <div>
        <b>${d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })} &middot; ${s.makes}/${s.attempts}</b>
        <small>${d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })} &middot;
          ${s.exchanges} exchange${s.exchanges === 1 ? '' : 's'}
          ${s.hasReport ? '&middot; <span style="color:var(--hot)">report</span>' : ''}
          ${s.demo ? '&middot; <span style="color:var(--mute)">example session</span>' : ''}</small>
      </div>
      <div class="score">${s.avgForm}</div>
    </button>`;
  }).join('') : '<span style="color:var(--mute)">No sessions yet.</span>';
  $('slist').querySelectorAll('.sitem').forEach((b) =>
    b.addEventListener('click', () => openSession(b.dataset.id)));
}

async function openSession(id) {
  const s = await api(`/api/sessions/${id}`);
  const sum = summarize(s.shots);
  const d = new Date(s.startedAt);
  $('mTitle').textContent = d.toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
  $('mSub').textContent = `${d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })} · ${s.endedAt ? 'completed' : 'in progress'} · ${companionById(s.companion).name}`;
  $('mStats').innerHTML = `
    <div class="stat"><b>${sum.attempts}</b><small>attempts</small></div>
    <div class="stat"><b>${sum.makes}</b><small>makes</small></div>
    <div class="stat"><b>${sum.avg.elbow}</b><small>elbow</small></div>
    <div class="stat"><b>${sum.avg.release}</b><small>release</small></div>
    <div class="stat"><b>${sum.avg.follow}</b><small>follow</small></div>`;
  $('mReportWrap').style.display = s.report ? '' : 'none';
  $('mReport').textContent = s.report || '';
  $('mQaWrap').style.display = s.exchanges.length ? '' : 'none';
  $('mQa').innerHTML = s.exchanges.map((e) =>
    `<div class="qa"><b>${esc(e.question)}</b>${esc(e.reply)}</div>`).join('');
  $('mShots').innerHTML = s.shots.length ? s.shots.map((x) =>
    `<span class="chip ${x.made === true ? 'made' : x.made === false ? 'missed' : ''}">#${x.index} &middot; ${x.overall}</span>`).join('')
    : '<span style="color:var(--mute);font-size:13px">No shots.</span>';
  // The replay: this session's recording, if one was kept, watched with her in her room, where she
  // calls each shot and answers questions about what is on screen.
  // The record opens at once; the button appears when the clip list (which asks the cloud too) is in.
  $('mWatch').hidden = true;
  $('modal').classList.add('open');
  const clip = (await allClips().catch(() => [])).find((c) => c.sessionId != null && String(c.sessionId) === String(id));
  $('mWatch').hidden = !clip;
  $('mWatch').textContent = `Watch the replay with ${companionById(S.settings.companion).name}`;
  $('mWatch').onclick = () => { $('modal').classList.remove('open'); go('companion'); watchTogether(clip); };
}

/* ------------------------------------------------------------------ vault */

let clipUrl = null;
async function renderVault() {
  const u = await vault.usage();
  $('vaultUsage').textContent = `${vault.fmtBytes(u.bytes)} of ${u.freeGb} GB on this device · ${u.count} clip${u.count === 1 ? '' : 's'}`;
  $('vaultBar').style.width = `${u.pct}%`;
  $('swRecord').className = `sw ${S.settings.recordSessions ? 'on' : ''}`;
  const clips = await vault.listClips();
  $('clips').innerHTML = clips.length ? clips.map((c) => `
    <button class="clip" data-id="${c.id}">
      <div class="thumb" style="${c.poster ? `background-image:url(${c.poster})` : ''}"><span>${vault.fmtDur(c.duration || 0)}</span></div>
      <b>${new Date(c.at).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })} &middot; ${new Date(c.at).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}</b>
      <small>${c.attempts ?? 0} attempts &middot; ${c.makes ?? 0} makes &middot; form ${c.form ?? '?'} &middot; ${vault.fmtBytes(c.size)}</small>
    </button>`).join('') : '<div class="card" style="color:var(--mute)">No videos yet. Turn on "Record my sessions". Then train. Your video shows up here.</div>';
  $('clips').querySelectorAll('.clip').forEach((b) => b.onclick = () => openClip(b.dataset.id));
  renderCloud();
}

const money = (n) => `$${Number(n).toFixed(2)}`;
async function renderCloud() {
  try {
    const { clips, terms } = await vault.cloudList();
    const used = terms.usedGb, inc = terms.includedGb;
    $('cloudMeter').innerHTML = `
      <div class="cm-row"><b>Cloud Vault</b><span>${used.toFixed(2)} GB used &middot; ${inc >= 1 ? `${Math.floor(inc)} GB included` : 'not included on this plan'}</span></div>
      <div class="bar" style="height:8px"><i style="width:${Math.min(100, (used / Math.max(inc, 0.01)) * 100)}%"></i></div>
      <div class="cm-row small"><span>Past the included storage: ${money(terms.overagePerGbMonth)} per GB per month, billed separately.</span>
        <span>${terms.overageGb > 0 ? `This month's storage bill: <b>${money(terms.estimatedOverageUsd)}</b>` : 'No storage charges this month.'}</span></div>`;
    $('cloudClips').innerHTML = clips.length ? clips.map((c) => `
      <button class="clip cloud" data-id="${c.id}">
        <div class="thumb" style="${c.poster ? `background-image:url(${c.poster})` : ''}"><span>${vault.fmtDur(c.duration || 0)}</span><em>CLOUD</em></div>
        <b>${new Date(c.at).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })} &middot; ${new Date(c.at).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}</b>
        <small>${c.attempts ?? 0} attempts &middot; ${c.makes ?? 0} makes &middot; form ${c.form ?? '?'} &middot; ${vault.fmtBytes(c.size)}</small>
      </button>`).join('') : '<div class="card" style="color:var(--mute)">Nothing in the cloud yet. Open a recording and choose "Save to cloud".</div>';
    $('cloudClips').querySelectorAll('.clip').forEach((b) => b.onclick = () => openCloudClip(clips.find((c) => c.id === b.dataset.id)));
  } catch (e) {
    $('cloudMeter').innerHTML = `<div class="cm-row small"><span>${e.message}</span></div>`;
    $('cloudClips').innerHTML = '';
  }
}

function openCloudClip(c) {
  if (!c) return;
  $('clipVideo').src = vault.cloudStreamUrl(c.id);
  $('clipTitle').textContent = new Date(c.at).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
  $('clipSub').textContent = `Cloud · ${vault.fmtDur(c.duration || 0)} · ${c.attempts ?? 0} attempts · ${c.makes ?? 0} makes · form ${c.form ?? '?'} · ${vault.fmtBytes(c.size)}`;
  $('clipDownload').href = vault.cloudStreamUrl(c.id); $('clipDownload').download = `hooper-buddy-${c.at.slice(0, 10)}.webm`;
  $('clipCloud').hidden = true;
  $('clipDelete').onclick = async () => {
    if (!await ui.confirm({ title: 'Delete this cloud recording?', body: 'It comes off every device you signed in on. This cannot be undone.', confirmText: 'Delete', danger: true })) return;
    await vault.cloudDelete(c.id); $('clipModal').classList.remove('open'); renderVault();
  };
  $('clipModal').classList.add('open');
}

async function openClip(id) {
  const c = await vault.getClip(id);
  if (!c) return;
  if (clipUrl) URL.revokeObjectURL(clipUrl);
  clipUrl = URL.createObjectURL(c.blob);
  $('clipVideo').src = clipUrl;
  $('clipTitle').textContent = new Date(c.at).toLocaleDateString(undefined, { weekday: 'long', month: 'long', day: 'numeric' });
  $('clipSub').textContent = `${vault.fmtDur(c.duration || 0)} · ${c.attempts ?? 0} attempts · ${c.makes ?? 0} makes · form ${c.form ?? '?'} · ${vault.fmtBytes(c.size)}`;
  $('clipDownload').href = clipUrl; $('clipDownload').download = `hooper-buddy-${c.at.slice(0, 10)}.webm`;
  $('clipDelete').onclick = async () => {
    if (!await ui.confirm({ title: 'Delete this recording?', body: 'It is only on this device, so once it goes it is gone.', confirmText: 'Delete', danger: true })) return;
    await vault.deleteClip(id); $('clipModal').classList.remove('open'); renderVault();
  };
  $('clipCloud').hidden = false; $('clipCloud').disabled = false; $('clipCloud').textContent = 'Save to cloud';
  $('clipCloud').onclick = async () => {
    $('clipCloud').disabled = true;
    try {
      await vault.cloudUpload(c, (p) => { $('clipCloud').textContent = `Uploading ${Math.round(p * 100)}%`; });
      $('clipCloud').textContent = 'Saved to cloud';
      toast('Recording saved to your Cloud Vault.');
      renderVault();
    } catch (e) { $('clipCloud').disabled = false; $('clipCloud').textContent = 'Save to cloud'; toast(e.message, true); }
  };
  $('clipModal').classList.add('open');
}

/* ------------------------------------------------------------ companion room */

let roomClip = null;
/** Every film they have, local and cloud, newest first. */
async function allClips() {
  const local = await vault.listClips().catch(() => []);
  let cloud = []; try { cloud = (await vault.cloudList()).clips.map((x) => ({ ...x, cloud: true })); } catch {}
  // A clip saved to the cloud is still on this device too: listed once, played from the device.
  const here = new Set(local.map((c) => c.id));
  return [...local, ...cloud.filter((c) => !here.has(c.id))].sort((a, b) => b.at.localeCompare(a.at));
}

/* ------------------------------------------------------ she drives the film
 * "Pull up my last film", "watch Tuesday's session", "show me the one from
 * yesterday": said from any screen, the companion opens her room, picks the
 * film and starts it herself, then calls the shots as they go by. The pick
 * is local and honest: newest unless they named a day, a place in the list
 * or "first". Nothing here asks the model, so it is instant and never wrong
 * about which film it opened; she says which one out loud so they can correct.
 */
const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
function pickClip(all, said) {
  const w = String(said || '').toLowerCase();
  if (!all.length) return null;
  if (/\b(first|oldest|earliest)\b/.test(w)) return all[all.length - 1];
  const nth = /\b(second|third|fourth|fifth)\b/.exec(w);
  if (nth) return all[['second', 'third', 'fourth', 'fifth'].indexOf(nth[1]) + 1] || null;
  const dayOf = (iso) => new Date(iso).toDateString();
  if (/\btoday\b/.test(w)) return all.find((c) => dayOf(c.at) === new Date().toDateString()) || null;
  if (/\byesterday\b/.test(w)) { const y = new Date(); y.setDate(y.getDate() - 1); return all.find((c) => dayOf(c.at) === y.toDateString()) || null; }
  const day = DAY_NAMES.findIndex((d) => new RegExp(`\\b${d}s?\\b`).test(w));
  if (day >= 0) return all.find((c) => new Date(c.at).getDay() === day) || null;
  return all[0];
}
async function openFilm(said) {
  const all = await allClips();
  const clip = pickClip(all, said);
  if (!clip) {
    const line = all.length ? 'I do not have a film from that day. Say which one and I will find it.' : 'No film yet. Train with the camera on and I will keep one.';
    transcribe('coach', line); speak(line, { onStart: () => companionState('talking'), onEnd: () => companionState('idle') });
    return;
  }
  if (S.view !== 'companion') { go('companion'); await new Promise((r) => setTimeout(r, 400)); }
  await watchTogether(clip);
}

async function renderCompanion() {
  const c = companionById(S.settings.companion);
  if (!roomBall) roomBall = mountCompanion($('roomBall'), S.settings.companion, { size: 340 }); else roomBall.swap(S.settings.companion);
  $('roomBg').style.setProperty('--aura', c.base);
  $('roomName').textContent = c.name;
  // No "on the right": the panel can be put away, and on a phone it is below.
  $('roomLine').textContent = `${c.story || c.blurb || ''} Tap the mic and talk to me, or pick a recorded session and we will watch it together.`;
  const all = await allClips();
  $('roomClips').innerHTML = all.length ? all.map((x) => `
    <button data-id="${x.id}" data-cloud="${x.cloud ? 1 : 0}"><div class="th" style="${x.poster ? `background-image:url(${x.poster})` : ''}"></div>
      <div><b>${new Date(x.at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} &middot; ${vault.fmtDur(x.duration || 0)}</b><small>${x.attempts ?? 0} shots &middot; ${x.makes ?? 0} makes &middot; form ${x.form ?? '?'}${x.cloud ? ' &middot; cloud' : ''}</small></div></button>`).join('')
    : '<p class="sub">No videos yet. Train with the camera on and one shows up here.</p>';
  $('roomClips').querySelectorAll('button').forEach((b) => b.onclick = () => watchTogether(all.find((x) => x.id === b.dataset.id)));
  renderThreads();
}

/* ------------------------------------------------------------ conversations
 * Every turn is already filed in the journal as a 'talk' entry (what they
 * said, what she said, when). Here they read back as threads, one per day,
 * newest first, like a chat history: tap a day, see the whole conversation.
 * Nothing new is stored; this is the journal, shown as talk.
 */
let threadsOpen = null;
async function renderThreads() {
  const root = $('roomThreads'); if (!root) return;
  let entries = [];
  try { const j = await api('/api/journal'); entries = (j.entries || j || []).filter((e) => e.kind === 'talk' && (e.you || e.text)); } catch { root.innerHTML = '<p class="sub">Could not load your conversations.</p>'; return; }
  if (!entries.length) { root.innerHTML = '<p class="sub">No conversations yet. Tap the mic and say hi.</p>'; return; }
  const days = new Map();
  for (const e of entries) { const d = String(e.at).slice(0, 10); if (!days.has(d)) days.set(d, []); days.get(d).push(e); }
  const label = (d) => { const t = new Date(`${d}T12:00:00`); const today = new Date().toISOString().slice(0, 10); const y = new Date(Date.now() - 864e5).toISOString().slice(0, 10); return d === today ? 'Today' : d === y ? 'Yesterday' : t.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' }); };
  root.innerHTML = [...days.entries()].slice(0, 60).map(([d, list]) => {
    const first = list[list.length - 1];
    const open = threadsOpen === d;
    return `<div class="thread${open ? ' open' : ''}" data-day="${d}">
      <button class="thread-head"><b>${esc(label(d))}</b><small>${list.length} turn${list.length === 1 ? '' : 's'} &middot; ${esc((first.you || first.text || '').slice(0, 60))}</small></button>
      ${open ? `<div class="thread-body">${[...list].reverse().map((e) => `
        ${e.you ? `<div class="turn you"><span>${esc(e.you)}</span><time>${new Date(e.at).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })}</time></div>` : ''}
        ${e.text ? `<div class="turn her"><span>${esc(e.text)}</span></div>` : ''}`).join('')}</div>` : ''}
    </div>`;
  }).join('');
  root.querySelectorAll('.thread-head').forEach((b) => { b.onclick = () => { const d = b.parentElement.dataset.day; threadsOpen = threadsOpen === d ? null : d; renderThreads(); }; });
}

/* --------------------------------------------------- watching film together
 * The recording starts with the session, and every shot is saved with the
 * moment it was scored, so a shot's place in the film is its time since the
 * session began. That gives her three things she did not have: she can jump
 * to a shot when asked, she can speak up on her own as each release goes by,
 * and at the end she can turn what she saw into a plan for next time.
 */
let roomSession = null;       // the session behind the clip, with its shots
let roomMarks = [];           // [{ index, t, shot }] in film seconds
let roomSaid = new Set();     // shots she has already commented on this viewing
const ROOM_LEAD = 2.5;        // seconds before the release to land on, so the whole shot plays
const NUM_WORDS = { one: 1, two: 2, three: 3, four: 4, five: 5, six: 6, seven: 7, eight: 8, nine: 9, ten: 10, eleven: 11, twelve: 12, first: 1, last: -1 };

async function watchTogether(clip) {
  if (!clip) return;
  roomClip = clip; roomSession = null; roomMarks = []; roomSaid = new Set();
  const v = $('roomVideo');
  if (clip.cloud) v.src = vault.cloudStreamUrl(clip.id);
  else { const c = await vault.getClip(clip.id); if (!c) return; v.src = URL.createObjectURL(c.blob); }
  $('roomScreen').hidden = false; document.querySelector('.room').classList.add('watching');
  // The small companion on the film itself, so she is in the picture while it plays.
  if (!miniBall) miniBall = mountCompanion($('roomMiniBall'), S.settings.companion, { size: 128 }); else miniBall.swap?.(S.settings.companion);
  $('roomMeta').textContent = `${new Date(clip.at).toLocaleString()} · ${clip.attempts ?? 0} attempts · ${clip.makes ?? 0} makes · form ${clip.form ?? '?'}`;
  $('roomShots').innerHTML = '';
  if (clip.sessionId) {
    try {
      roomSession = await api(`/api/sessions/${clip.sessionId}`);
      const t0 = Date.parse(roomSession.startedAt);
      roomMarks = (roomSession.shots || []).map((shot) => ({ index: shot.index, shot, t: Math.max(0, (Date.parse(shot.at) - t0) / 1000 - ROOM_LEAD) })).filter((m) => Number.isFinite(m.t));
      $('roomShots').innerHTML = roomMarks.map((m) => `<button data-i="${m.index}" class="${m.shot.made === true ? 'made' : m.shot.made === false ? 'miss' : ''}" title="Shot ${m.index}, form ${m.shot.overall}">${m.index}<small>${m.shot.overall}</small></button>`).join('');
      $('roomShots').querySelectorAll('button').forEach((b) => { b.onclick = () => seekShot(Number(b.dataset.i)); });
    } catch { /* an older clip with no session behind it still plays */ }
  }
  // A phone may refuse to start a video with sound when no tap started it,
  // and a voice request is not a tap (iOS always refuses). Then it plays
  // muted, which every phone allows, and one tap on the button brings the
  // sound in. Before, the refusal was swallowed and the film just sat there.
  v.muted = false;
  const playing = await v.play().then(() => true).catch(() => false);
  $('roomSound').hidden = true;
  if (!playing) {
    v.muted = true;
    await v.play().catch(() => {});
    $('roomSound').hidden = false;
  }
  const line = roomMarks.length
    ? `Aight, pulling it up. ${roomMarks.length} shots from ${new Date(clip.at).toLocaleDateString(undefined, { month: 'long', day: 'numeric' })}. I will call them as they come. Say shot three, next shot, note this, or plan, whenever you want.`
    : `Aight, pulling it up. ${clip.attempts ?? 0} shots from ${new Date(clip.at).toLocaleDateString(undefined, { month: 'long', day: 'numeric' })}. Say pause whenever you want to talk about a moment.`;
  transcribe('coach', line);
  speak(line, { onStart: () => companionState('talking'), onEnd: () => companionState('idle') });
}

/** What she says as a shot goes by. Short, true, and one cue at most. */
function replayLine(shot) {
  const sc = shot.scores || {};
  const worst = ['elbow', 'release', 'follow'].filter((k) => Number.isFinite(sc[k])).sort((a, b) => sc[a] - sc[b])[0];
  const cue = worst && sc[worst] < 60 ? { elbow: 'Elbow drifted there.', release: 'Release came out early.', follow: 'Hold that follow through.' }[worst] : '';
  const result = shot.made === true ? 'Dropped.' : shot.made === false ? 'Missed.' : '';
  const grade = shot.overall >= 85 ? 'That is the one.' : shot.overall >= 70 ? 'Solid form.' : cue ? '' : 'Rough one.';
  return `Shot ${shot.index}. ${result} ${grade} ${cue}`.replace(/\s+/g, ' ').trim();
}

/** Her unprompted voice as the film plays. One comment per shot, never over her own voice. */
const ROOM_CALL_S = 6;
function roomTick() {
  const v = $('roomVideo');
  if (!roomMarks.length || v.paused || companionSpeaking()) return;
  // Up to ROOM_CALL_S after the shot: she is often still talking (her opening line is long), and a
  // 2.5 s window meant the first shots, and shots close together, were never called (2026-10-10).
  const m = roomMarks.find((x) => !roomSaid.has(x.index) && v.currentTime >= x.t + ROOM_LEAD - 0.4 && v.currentTime < x.t + ROOM_LEAD + ROOM_CALL_S);
  if (!m) return;
  roomSaid.add(m.index);
  const line = replayLine(m.shot);
  transcribe('coach', line);
  speak(line, { kind: 'film', onStart: () => companionState('talking'), onEnd: () => companionState('idle') });
}

/** Jump the film to a shot by number; negative counts from the end. */
function seekShot(n) {
  if (!roomMarks.length) { const l = 'I have no shot marks for this one.'; transcribe('coach', l); speak(l); return; }
  const i = n < 0 ? roomMarks.length + 1 + n : n;
  const m = roomMarks.find((x) => x.index === i);
  if (!m) { const l = `There are ${roomMarks.length} shots in this one.`; transcribe('coach', l); speak(l); return; }
  const v = $('roomVideo'); v.currentTime = m.t; v.play().catch(() => {});
  roomSaid.delete(m.index);          // she calls it again as it goes by
  $('roomShots').querySelectorAll('button').forEach((b) => b.classList.toggle('on', Number(b.dataset.i) === m.index));
}

/** The shot the film is on right now, or the one just gone by. */
const currentShot = () => { const t = $('roomVideo').currentTime; return [...roomMarks].reverse().find((m) => t >= m.t) || null; };

/** A note in their words, filed to the journal with the film and the shot it was about. */
async function roomNote(text) {
  const m = currentShot();
  const stamp = `Film ${new Date(roomClip.at).toLocaleDateString()}${m ? `, shot ${m.index}` : ''}, ${vault.fmtDur($('roomVideo').currentTime)}`;
  try {
    await api('/api/journal', { method: 'POST', body: JSON.stringify({ text: `${text}\n\n${stamp}` }) });
    const l = 'Noted.'; transcribe('coach', l); speak(l);
  } catch (e) { toast(e.message, true); }
}

/** What to do next time, from what she just watched, spoken and kept. */
async function nextSessionPlan() {
  if (!roomClip?.sessionId) return;
  const v = $('roomVideo'); v.pause();
  companionState('thinking');
  const voice = speakStream({ onStart: () => companionState('talking'), onEnd: () => companionState('idle') });
  const line = transcribeStream('coach');
  try {
    const { text } = await streamApi(`/api/sessions/${roomClip.sessionId}/coach?stream=1`,
      { question: 'We just watched the film of this session together. Give me a plan for my next session: the one thing to fix first, one drill for it, and how many reps. Three short sentences, spoken, no list.' },
      { onText: (t) => { voice.push(t); line.push(t); } });
    voice.end(); line.end(text);
    if (text) await api('/api/journal', { method: 'POST', body: JSON.stringify({ text: `Next session plan, from the film of ${new Date(roomClip.at).toLocaleDateString()}:\n\n${text}` }) });
  } catch (e) { voice.end(); line.end(''); toast(e.message, true); }
}

async function askAboutMoment() {
  if (!roomClip) return;
  const v = $('roomVideo'); v.pause();
  const total = Number.isFinite(v.duration) ? v.duration : (roomClip.duration || 0);
  const at = vault.fmtDur(v.currentTime), dur = vault.fmtDur(total);
  let shots = '';
  try { const s = await api(`/api/sessions/${roomClip.sessionId}`); shots = s.shots.map((x) => `#${x.index} form ${x.overall}${x.element ? ` (${x.element})` : ''}${x.made === true ? ' made' : x.made === false ? ' missed' : ''}`).join(', '); } catch {}
  const here = currentShot();
  const msg = `We are watching my recorded session from ${new Date(roomClip.at).toLocaleDateString()} together on the screen, paused at ${at} of ${dur}.${here ? ` The shot on screen right now is #${here.index}: form ${here.shot.overall}${here.shot.scores ? ` (elbow ${here.shot.scores.elbow}, release ${here.shot.scores.release}, follow through ${here.shot.scores.follow})` : ''}${here.shot.made === true ? ', it went in' : here.shot.made === false ? ', it missed' : ''}${here.shot.zone ? `, from ${here.shot.zone} range about ${here.shot.distanceFt} feet (camera estimate)` : ''}.` : ''} The clip runs the whole session start to finish, so ${at} is about ${Math.round((v.currentTime / (total || 1)) * 100)} percent of the way through. Session data: ${roomClip.attempts ?? 0} attempts, ${roomClip.makes ?? 0} makes, average form ${roomClip.form ?? '?'}. Per shot: ${shots || 'not available'}. Tell me what to look at right here, what I probably cannot see myself, and one thing to try on the next rep.`;
  // Straight to her: "my recorded session from <date>" reads as "open a film" to the page's own check,
  // which reloaded the newest film from the start instead of asking her (2026-10-10).
  $('talkIn').value = msg; await talk({ direct: true });
}

/* ---------------------------------------------------------------- journal */

/* Starters a player can tap. Each one goes into the pad as a heading they then
 * answer, so a blank page is never the thing standing between them and
 * writing. Player language, not a worksheet and not a therapist. */
const JOURNAL_PROMPTS = [
  'How practice felt today',
  'What broke down',
  'What clicked',
  'Nerves before this game',
  'What to fix next session',
  'Something coach said',
];
const JOURNAL_KIND = { note: 'Journal', talk: 'Talked', session: 'Session', body: 'Body' };
const JOURNAL_MAX = 4000;        // the cap worker/api.js slices the text at
const DRAFT_KEY = 'hb.journal.draft';
let jShown = 25;                 // how many entries are on the page right now
let jDraftTimer = null;
let jPadWidth = 0;               // the width the pad was last measured at

const jWords = (s) => (String(s ?? '').match(/\S+/g) || []).length;

/** Grow the pad to fit what is in it. Nobody writes a lot into a 3 line box. */
function growPad() {
  const t = $('jText');
  // A hidden textarea measures zero, so leave it alone until the view opens
  // and renderJournal measures it for real.
  if (!t || !t.offsetParent) return;
  t.style.height = 'auto';
  t.style.height = `${t.scrollHeight}px`;
  jPadWidth = t.clientWidth;
}

/* The height above is a pixel number, so anything that changes the column
 * leaves it stale: the rail opening, a window dragged narrower, a phone turned
 * on its side. The pad hides its overflow, so a stale height swallows the end
 * of what they wrote with no scrollbar to get it back. Measure again whenever
 * the pad changes WIDTH. Height changes are ours, and re-measuring on those
 * would just chase itself. */
function watchPad() {
  const t = $('jText');
  if (!t) return;
  if (!('ResizeObserver' in window)) { window.addEventListener('resize', growPad); return; }
  new ResizeObserver(() => { if (t.clientWidth !== jPadWidth) growPad(); }).observe(t);
}

function updateCount() {
  const v = $('jText').value;
  const n = jWords(v);
  const left = JOURNAL_MAX - v.length;
  $('jCount').innerHTML = `${n} word${n === 1 ? '' : 's'}${left <= 500 ? ` <b class="jw-left">${left} characters left</b>` : ''}`;
}

/* A closed tab should never cost somebody an entry, so what they are writing
 * goes to this browser as they type and is dropped the moment it is really
 * saved. Storage can be off, and an entry still has to save when it is. */
function queueDraft() {
  clearTimeout(jDraftTimer);
  jDraftTimer = setTimeout(() => {
    const flag = $('jDraft');
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify({ text: $('jText').value, mood: $('jMood').value }));
    } catch { return; }
    flag.hidden = !$('jText').value.trim();
    clearTimeout(flag.t);
    flag.t = setTimeout(() => { flag.hidden = true; }, 2200);
  }, 700);
}

function clearDraft() {
  clearTimeout(jDraftTimer);
  try { localStorage.removeItem(DRAFT_KEY); } catch { /* nothing was stored */ }
  $('jDraft').hidden = true;
}

function restoreDraft() {
  let d = null;
  try { d = JSON.parse(localStorage.getItem(DRAFT_KEY) || 'null'); } catch { return; }
  if (!d || typeof d.text !== 'string' || !d.text.trim() || $('jText').value.trim()) return;
  $('jText').value = d.text.slice(0, JOURNAL_MAX);
  if (d.mood) $('jMood').value = d.mood;
}

/** A tapped starter lands as its own line with room under it to answer. */
function usePrompt(line) {
  const t = $('jText');
  const before = t.value.replace(/\s+$/, '');
  t.value = `${before ? `${before}\n\n` : ''}${line}\n\n`.slice(0, JOURNAL_MAX);
  t.focus();
  t.selectionStart = t.selectionEnd = t.value.length;
  growPad(); updateCount(); queueDraft();
}

function paintPrompts() {
  const box = $('jPrompts');
  box.innerHTML = JOURNAL_PROMPTS.map((p, i) => `<button type="button" class="jw-prompt" data-p="${i}">${esc(p)}</button>`).join('');
  box.querySelectorAll('[data-p]').forEach((b) => {
    b.onclick = () => usePrompt(JOURNAL_PROMPTS[Number(b.dataset.p)] || '');
  });
}

/** Today, Yesterday, then the day by name: a journal reads by date. */
function dayHeading(at) {
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return 'Earlier';
  const midnight = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((midnight(new Date()) - midnight(d)) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  return d.toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' });
}

function entryHtml(e) {
  const text = String(e.text ?? '');
  // Long entries fold instead of truncating: the whole thing is one tap away.
  const long = text.length > 900;
  const when = new Date(e.at);
  const time = Number.isNaN(when.getTime()) ? '' : when.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
  return `<article class="jentry">
      <div class="je-head">
        <span class="k ${esc(e.kind)}">${esc(JOURNAL_KIND[e.kind] || e.kind)}</span>
        ${e.mood ? `<span class="je-mood">${esc(e.mood)}</span>` : ''}
        <span class="je-time">${esc(time)}</span>
      </div>
      ${e.you ? `<p class="you">You said: ${esc(e.you)}</p>` : ''}
      <div class="je-tx${e.kind === 'note' ? ' ruled' : ''}${long ? ' clip' : ''}"><p>${esc(text)}</p></div>
      ${long ? '<button class="je-more" type="button">Read all of it</button>' : ''}
      ${media.attachmentsHtml(e)}
      ${e.attempts != null ? `<small>${Number(e.attempts) || 0} attempts, ${Number(e.makes) || 0} makes</small>` : ''}
      <button class="x" data-id="${esc(e.id)}" title="Delete this entry" aria-label="Delete this entry">${icons.icon('close', 13)}</button>
    </article>`;
}

async function renderJournal() {
  // The pad is measured here, not at boot: a hidden textarea has no height.
  growPad(); updateCount();
  let data;
  try { data = await api('/api/journal'); }
  catch (e) { $('jList').innerHTML = `<p class="jf-empty">Could not load your journal: ${esc(e.message)}</p>`; return; }
  const { entries, days, body } = data;

  const last = body[0];
  // Adults only, matching worker/health.js. A child sees their numbers, never a
  // verdict derived from them.
  const showBmi = S.settings.ageBandChosen === true && S.settings.ageBand === 'adult';
  $('bLast').textContent = last ? `Last log ${new Date(last.at).toLocaleDateString()}: ${[last.weightKg && `${last.weightKg} kg`, last.heightCm && `${last.heightCm} cm`, showBmi && last.bmi && `BMI ${last.bmi}`].filter(Boolean).join(' · ')}` : 'No body log yet.';
  if (last?.heightCm && !$('bH').value) $('bH').value = last.heightCm;

  const today = new Date(); const cells = [];
  // A trained day is lit. A day they only wrote on gets the quieter mark, so
  // the grid says what the line under it promises and the two still read apart.
  for (let i = 27; i >= 0; i--) { const d = new Date(today); d.setDate(today.getDate() - i); const key = d.toISOString().slice(0, 10); const day = days.find((x) => x.date === key); cells.push(`<span class="${day?.trained ? 't' : day?.entries ? 'w' : ''}" title="${key}${day ? ` · ${day.entries} entr${day.entries === 1 ? 'y' : 'ies'}` : ''}">${d.getDate()}</span>`); }
  $('jDays').innerHTML = cells.join('');

  $('jCountLine').textContent = entries.length ? `${entries.length} entr${entries.length === 1 ? 'y' : 'ies'}` : '';
  if (!entries.length) {
    $('jList').innerHTML = '<p class="jf-empty">Nothing here yet. Write an entry up there, record a voice note, log your body, or just talk to your companion: conversations file themselves here.</p>';
  } else {
    let html = '', heading = '';
    for (const e of entries.slice(0, jShown)) {
      const h = dayHeading(e.at);
      if (h !== heading) { heading = h; html += `<h4 class="jf-day">${esc(h)}</h4>`; }
      html += entryHtml(e);
    }
    $('jList').innerHTML = html;
  }
  const more = Math.max(0, entries.length - jShown);
  $('jMore').hidden = !more;
  $('jMore').textContent = `Show older entries (${more} more)`;

  media.bindEntries($('jList'));
  $('jList').querySelectorAll('.je-more').forEach((b) => b.onclick = () => {
    const box = b.previousElementSibling;
    if (!box) return;
    box.classList.toggle('clip');
    b.textContent = box.classList.contains('clip') ? 'Read all of it' : 'Show less';
  });
  $('jList').querySelectorAll('.x').forEach((b) => b.onclick = async () => {
    if (!await ui.confirm({ title: 'Delete this entry?', body: 'It leaves your journal and your companion stops remembering it.', confirmText: 'Delete', danger: true })) return;
    try { await api(`/api/journal/${b.dataset.id}`, { method: 'DELETE' }); renderJournal(); }
    catch (e) { toast(e.message, true); }
  });
}

/* ------------------------------------------------------------------ music */

let artistsCache = null;
const openPlaylists = new Set();        // which drawers are open, kept across repaints
const REPEAT_LABEL = { off: 'Repeat off', all: 'Repeat all', one: 'Repeat one' };
// repeat one has the 1 drawn into the glyph, so the two modes never look alike.
const repeatIcon = (repeat, size) => icons.icon(repeat === 'one' ? 'repeat-one' : 'repeat', size);

/** The line under a title: who made it, then what it is. The station does not
 *  always send an artist, so say that plainly instead of leaving a stray bpm. */
function trackMeta(t) {
  const bits = [t.artist
    ? `<span class="who">${esc(t.artist)}</span>`
    : '<span class="none">Artist not listed</span>'];
  if (t.genre) bits.push(esc(t.genre));
  if (t.bpm) bits.push(`${Math.round(t.bpm)} bpm`);
  return bits.join(' &middot; ');
}

async function renderMusic() {
  await radio.load().catch(() => {});
  $('mMode').value = S.settings.musicMode || 'duck';
  paintNow();
  const keepScroll = $('mTracks').scrollTop;
  $('mTracks').innerHTML = radio.tracks.length ? radio.tracks.map((t, i) => `
    <button class="track" data-i="${i}">
      ${t.art ? `<img src="${esc(t.art)}" alt="">` : `<span class="ph">${icons.icon('music', 20)}</span>`}
      <div><b>${esc(t.title)}</b><small>${trackMeta(t)}</small></div>
      <span class="flag-slot"></span>
      <span class="add" data-add="${i}" title="Save to a playlist">+</span>
    </button>`).join('')
    : '<p class="sub">The station is not answering right now. Try again in a minute.</p>';
  $('mTracks').scrollTop = keepScroll;
  $('mTracks').querySelectorAll('.track').forEach((b) => b.onclick = (e) => {
    if (e.target.dataset.add) return saveToPlaylist(radio.tracks[Number(e.target.dataset.add)]);
    radio.playAt(Number(b.dataset.i)).then(afterMusicChange).catch((err) => toast(`Music: ${err.message}`, true));
  });
  markNowPlaying();
  renderPlaylists();
  if (!artistsCache) {
    try { const r = await fetch('https://soultownmusic.com/api/v1/artists'); const j = await r.json(); artistsCache = j.artists || j; } catch { artistsCache = []; }
  }
  // A few SoulTown taglines arrive with a long dash in them. House style has no
  // long dashes, and this is copy inside the app, so it reads as a comma here.
  const prose = (t) => esc(String(t).replace(/\s*[\u2013\u2014]\s*/g, ', '));
  // Count from the catalog, not from the artist record: this is how many of
  // theirs the player can actually press play on.
  const counts = {};
  radio.tracks.forEach((t) => { if (t.artistId) counts[t.artistId] = (counts[t.artistId] || 0) + 1; });
  $('mArtists').innerHTML = artistsCache.length ? artistsCache.map((a) => {
    const n = counts[a.id] || 0;
    return `<a class="artist" href="${esc(a.profile_url || 'https://soultownmusic.com')}" target="_blank" rel="noopener">
      ${a.portrait ? `<img src="${esc(a.portrait)}" alt="">` : `<span class="ph">${esc((a.name || '?').slice(0, 1))}</span>`}
      <b>${esc(a.name || 'SoulTown artist')}</b>
      ${a.lane ? `<span class="lane">${esc(a.lane)}</span>` : ''}
      ${a.tagline ? `<span class="tag">${prose(a.tagline)}</span>` : ''}
      <span class="songs">${n} ${n === 1 ? 'track' : 'tracks'} on the station</span>
    </a>`;
  }).join('') : '<p class="sub">Artist profiles are loading from SoulTownMusic.com.</p>';
}

function paintNow() {
  const t = radio.current;
  $('npTitle').textContent = t?.title || 'Not playing';
  $('npSub').innerHTML = t ? trackMeta(t) : 'Press play to start the station';
  const art = $('npArt'); if (t?.art) { art.src = t.art; art.hidden = false; } else art.hidden = true;
  $('mPlay').innerHTML = icons.icon(radio.playing ? 'pause' : 'play', 17);
  $('mPlay').title = radio.playing ? 'Pause' : 'Play';
  paintModes();
}

/** Shuffle and repeat, painted the same way in the music tab and the corner
 *  player, plus the same thing in words so a lit icon is never a guess. */
function paintModes() {
  if (!radio) return;
  const { shuffle, repeat, queueName } = radio.modes;
  const set = (el, on, label) => {
    if (!el) return;
    el.classList.toggle('on', on);
    el.setAttribute('aria-pressed', String(on));
    el.title = label;
  };
  set($('mShuffle'), shuffle, shuffle ? 'Shuffle on' : 'Shuffle off');
  set($('mRepeat'), repeat !== 'off', REPEAT_LABEL[repeat]);
  $('mRepeat').innerHTML = repeatIcon(repeat, 17);
  $('mRepeat').setAttribute('aria-label', REPEAT_LABEL[repeat]);
  $('mModes').innerHTML = `Shuffle <b>${shuffle ? 'on' : 'off'}</b> &middot; Repeat <b>${repeat}</b> &middot; Playing from <b>${esc(queueName)}</b>`;
  set($('radioShuffle'), shuffle, shuffle ? 'Shuffle on' : 'Shuffle off');
  set($('radioRepeat'), repeat !== 'off', REPEAT_LABEL[repeat]);
  if ($('radioRepeat')) $('radioRepeat').querySelector('i').innerHTML = repeatIcon(repeat, 15);
  if ($('radioRepeatLab')) $('radioRepeatLab').textContent = REPEAT_LABEL[repeat];
}

/** Move the now playing marker without rebuilding the lists, so a crossfade
 *  never scrolls the station back to the top under the player's thumb. */
function markNowPlaying() {
  const id = radio.current?.id;
  $('mTracks').querySelectorAll('.track').forEach((b) => {
    const t = radio.tracks[Number(b.dataset.i)];
    const on = !!t && !!id && t.id === id;
    b.classList.toggle('on', on);
    if (on) b.setAttribute('aria-current', 'true'); else b.removeAttribute('aria-current');
    const slot = b.querySelector('.flag-slot');
    slot.className = on ? 'flag-slot flag' : 'flag-slot';
    slot.innerHTML = on ? '<i></i>Now playing' : '';
  });
  $('mPlaylists').querySelectorAll('.pl-track').forEach((row) =>
    row.classList.toggle('on', !!id && row.dataset.tid === id));
}

/** After anything that changes what is playing, but not what is saved. */
function afterMusicChange() {
  if (S.view !== 'music') return;
  paintNow();
  markNowPlaying();
}

async function renderPlaylists() {
  let pls;
  try { pls = await api('/api/playlists'); }
  catch (e) { $('mPlaylists').innerHTML = `<p class="sub">Playlists did not load: ${esc(e.message)}</p>`; return; }
  if (!pls.length) {
    $('mPlaylists').innerHTML = '<p class="sub">Nothing saved yet. Hit + on a track you like.</p>';
    return;
  }
  $('mPlaylists').innerHTML = pls.map((p) => {
    const open = openPlaylists.has(p.id);
    const n = p.tracks.length;
    return `<div class="pl${open ? ' open' : ''}" data-id="${esc(p.id)}">
      <button class="pl-head" data-toggle aria-expanded="${open}"><i class="caret">${icons.icon('chevron', 11)}</i><b>${esc(p.name)}</b><small>${n} ${n === 1 ? 'track' : 'tracks'}</small></button>
      <div class="pl-body"${open ? '' : ' hidden'}>
        <div class="pl-acts">
          <button data-act="play"${n ? '' : ' disabled'}>Play all</button>
          <button data-act="rename">Rename</button>
          <button data-act="delete" class="danger">Delete playlist</button>
        </div>
        ${n ? p.tracks.map((t, i) => `
          <div class="pl-track" data-tid="${esc(t.id || '')}">
            <button class="go" data-play="${i}"><i>${icons.icon('play', 12)}</i><span class="t"><b>${esc(t.title || 'Untitled')}</b> <em>${esc(t.artist || 'Artist not listed')}</em></span></button>
            ${t.id ? `<button class="x" data-drop="${esc(t.id)}" title="Remove from this playlist">${icons.icon('close', 13)}</button>` : ''}
          </div>`).join('')
      : '<p class="pl-empty">This one is empty. Hit + on a track in the station to fill it.</p>'}
      </div>
    </div>`;
  }).join('');
  bindPlaylists(pls);
  markNowPlaying();
}

function bindPlaylists(pls) {
  const byId = new Map(pls.map((p) => [p.id, p]));
  $('mPlaylists').querySelectorAll('.pl').forEach((el) => {
    const pl = byId.get(el.dataset.id);
    if (!pl) return;
    el.querySelector('[data-toggle]').onclick = () => {
      const open = !openPlaylists.has(pl.id);
      if (open) openPlaylists.add(pl.id); else openPlaylists.delete(pl.id);
      el.classList.toggle('open', open);
      el.querySelector('.pl-body').hidden = !open;
      el.querySelector('.pl-head').setAttribute('aria-expanded', String(open));
    };
    el.querySelectorAll('[data-play]').forEach((b) => b.onclick = () => playFromPlaylist(pl, Number(b.dataset.play)));
    el.querySelectorAll('[data-act]').forEach((b) => b.onclick = () => playlistAction(b.dataset.act, pl));
    el.querySelectorAll('[data-drop]').forEach((b) => b.onclick = () => dropTrack(pl, b.dataset.drop));
  });
}

function playFromPlaylist(pl, at) {
  return radio.playQueue(pl.tracks, at, pl.name)
    .then(afterMusicChange)
    .catch((e) => toast(`Music: ${e.message}`, true));
}

async function playlistAction(act, pl) {
  if (act === 'play') return playFromPlaylist(pl, 0);
  if (act === 'rename') return renamePlaylist(pl);
  const yes = await ui.confirm({
    title: `Delete ${pl.name}?`,
    body: 'The playlist goes. The songs stay on the station.',
    confirmText: 'Delete', danger: true,
  });
  if (!yes) return;
  try { await api(`/api/playlists/${pl.id}`, { method: 'DELETE' }); }
  catch (e) { return toast(e.message, true); }
  openPlaylists.delete(pl.id);
  toast(`Deleted ${pl.name}.`);
  renderPlaylists();
}

/* There is no rename route. POST /api/playlists creates or appends by name, so
 * a rename is the same tracks posted under the new name and the old playlist
 * dropped. Two playlists that end up with one name become one. */
async function renamePlaylist(pl) {
  const name = await ui.prompt({
    title: 'Rename playlist',
    body: 'If you already have a playlist with this name, the two join up.',
    label: 'Playlist name', value: pl.name, confirmText: 'Rename',
  });
  if (!name || name === pl.name) return;
  try {
    if (!pl.tracks.length) await api('/api/playlists', { method: 'POST', body: JSON.stringify({ name }) });
    for (const t of pl.tracks) await api('/api/playlists', { method: 'POST', body: JSON.stringify({ name, track: t }) });
    await api(`/api/playlists/${pl.id}`, { method: 'DELETE' });
  } catch (e) { return toast(e.message, true); }
  openPlaylists.delete(pl.id);
  toast(`Renamed to ${name}.`);
  renderPlaylists();
}

async function dropTrack(pl, trackId) {
  const t = pl.tracks.find((x) => x.id === trackId);
  const yes = await ui.confirm({
    title: 'Remove this track?',
    body: `${t?.title || 'This track'} leaves ${pl.name}. It stays on the station.`,
    confirmText: 'Remove', danger: true,
  });
  if (!yes) return;
  try { await api(`/api/playlists/${pl.id}?track=${encodeURIComponent(trackId)}`, { method: 'DELETE' }); }
  catch (e) { return toast(e.message, true); }
  renderPlaylists();
}

async function saveToPlaylist(t) {
  if (!t) return toast('Nothing playing yet.');
  const name = await ui.prompt({
    title: 'Save to a playlist',
    body: 'A new name, or the name of one you already have.',
    label: 'Playlist name', value: 'Workout mix', confirmText: 'Save',
  });
  if (!name) return;
  const track = { id: t.id, title: t.title, artist: t.artist, art: t.art, audio_url: t.audio_url };
  try { await api('/api/playlists', { method: 'POST', body: JSON.stringify({ name, track }) }); }
  catch (e) { return toast(e.message, true); }
  toast(`Saved to ${name}.`);
  if (S.view === 'music') renderPlaylists();
}

/* ------------------------------------------------------------------ brain */

let brainState = null;
const brainPos = new Map(); // node id -> {x,y}, kept so the map does not jump on reopen
const cssVar = (n) => getComputedStyle(document.documentElement).getPropertyValue(n).trim();

async function renderBrain() {
  const { nodes, edges } = await api('/api/memory');
  const canvas = $('brain');
  const rect = canvas.parentElement.getBoundingClientRect();
  const dpr = window.devicePixelRatio || 1;
  canvas.width = rect.width * dpr; canvas.height = rect.height * dpr;
  const ctx = canvas.getContext('2d');
  ctx.scale(dpr, dpr);
  const W = rect.width, H = rect.height;

  const COLOR = { self: '#ff5a1f', session: '#2ea8ff', skill: '#22d97a', talk: '#9d6bff' };
  const P = nodes.map((n, i) => {
    const kept = brainPos.get(n.id);
    return {
      ...n,
      x: kept?.x ?? W / 2 + Math.cos((i / nodes.length) * Math.PI * 2) * (60 + Math.random() * 130),
      y: kept?.y ?? H / 2 + Math.sin((i / nodes.length) * Math.PI * 2) * (60 + Math.random() * 110),
      vx: 0, vy: 0,
    };
  });
  const index = new Map(P.map((p) => [p.id, p]));
  const E = edges.map((e) => ({ a: index.get(e.from), b: index.get(e.to), s: e.strength })).filter((e) => e.a && e.b);

  let drag = null, hover = null;
  brainState = { reheat: () => P.forEach((p) => { p.vx += (Math.random() - .5) * 26; p.vy += (Math.random() - .5) * 26; }) };

  function step() {
    // repulsion
    for (let i = 0; i < P.length; i++) for (let j = i + 1; j < P.length; j++) {
      const a = P[i], b = P[j];
      let dx = b.x - a.x, dy = b.y - a.y;
      let d2 = dx * dx + dy * dy || 0.01;
      const f = 2600 / d2;
      const d = Math.sqrt(d2);
      const ux = dx / d, uy = dy / d;
      a.vx -= ux * f; a.vy -= uy * f; b.vx += ux * f; b.vy += uy * f;
    }
    // springs
    for (const e of E) {
      const dx = e.b.x - e.a.x, dy = e.b.y - e.a.y;
      const d = Math.hypot(dx, dy) || 0.01;
      const target = 108 / (e.s + 0.25);
      const f = (d - target) * 0.012 * (e.s + 0.4);
      const ux = dx / d, uy = dy / d;
      e.a.vx += ux * f; e.a.vy += uy * f; e.b.vx -= ux * f; e.b.vy -= uy * f;
    }
    for (const p of P) {
      if (p === drag) continue;
      p.vx += (W / 2 - p.x) * 0.0016; p.vy += (H / 2 - p.y) * 0.0016;
      p.vx *= 0.9; p.vy *= 0.9;
      p.x += p.vx; p.y += p.vy;
      brainPos.set(p.id, { x: p.x, y: p.y });
      const m = p.weight + 12;
      p.x = Math.max(m, Math.min(W - m, p.x)); p.y = Math.max(m, Math.min(H - m, p.y));
    }
  }

  function paint() {
    ctx.clearRect(0, 0, W, H);
    const day = document.documentElement.dataset.theme === 'day';
    const ink = cssVar('--ink') || '#e8eef7';
    for (const e of E) {
      ctx.strokeStyle = day ? `rgba(40,60,90,${0.18 + e.s * 0.35})` : `rgba(120,150,190,${0.08 + e.s * 0.2})`;
      ctx.lineWidth = 0.6 + e.s * 1.6;
      ctx.beginPath(); ctx.moveTo(e.a.x, e.a.y); ctx.lineTo(e.b.x, e.b.y); ctx.stroke();
    }
    for (const p of P) {
      const c = COLOR[p.kind] || '#8395ad';
      const isHot = p === hover || p === drag;
      ctx.shadowColor = c; ctx.shadowBlur = isHot ? 26 : 12;
      ctx.fillStyle = c;
      ctx.beginPath(); ctx.arc(p.x, p.y, p.weight * (isHot ? 1.16 : 1), 0, Math.PI * 2); ctx.fill();
      ctx.shadowBlur = 0;
      if (p.weight > 6 || isHot) {
        ctx.fillStyle = ink;
        ctx.font = `800 ${Math.max(11, Math.min(14, p.weight * 0.6))}px Nunito,sans-serif`;
        ctx.textAlign = 'center';
        ctx.fillText(p.label, p.x, p.y + p.weight + 14);
      }
    }
  }

  const at = (ev) => {
    const r = canvas.getBoundingClientRect();
    const x = ev.clientX - r.left, y = ev.clientY - r.top;
    return P.find((p) => Math.hypot(p.x - x, p.y - y) < p.weight + 6);
  };
  canvas.onmousedown = (e) => { drag = at(e); };
  canvas.onmouseup = () => { drag = null; };
  canvas.onmousemove = (e) => {
    const r = canvas.getBoundingClientRect();
    hover = at(e);
    if (drag) { drag.x = e.clientX - r.left; drag.y = e.clientY - r.top; drag.vx = drag.vy = 0; }
    canvas.style.cursor = hover ? 'pointer' : drag ? 'grabbing' : 'default';
    if (hover) showCard(hover);
    else if (pinned) showCard(pinned);
    else $('nodecard').className = 'nodecard';
  };
  // Every node does something: sessions and conversations open the session,
  // skills and "you" pin their card open so the numbers can be read.
  let pinned = null;
  canvas.onclick = (e) => {
    const n = at(e);
    if (!n) { pinned = null; $('nodecard').className = 'nodecard'; return; }
    if (n.ref) return openSession(n.ref);
    pinned = n; hover = n; showCard(n);
  };
  function showCard(n) {
    const card = $('nodecard');
    card.className = 'nodecard on';
    card.innerHTML = `<b>${esc(n.label)}</b><small>${esc(n.meta || (n.kind === 'self' ? 'That is you. Everything here is what your companion has learned about your game.' : n.kind))}</small>${
      n.score !== undefined ? `<div style="margin-top:9px" class="bar"><i style="width:${Number(n.score) || 0}%"></i></div>` : ''}`;
  }

  (function tick() {
    if (S.view !== 'brain') return;
    step(); paint(); requestAnimationFrame(tick);
  })();
}

/* --------------------------------------------------------------- settings */

async function renderSettings() {
  const { companions, personalities, languages, ageBands, allowed } = S.config;
  const s = S.settings;

  $('pickCompanion').innerHTML = companions.map((c) => `
    <button data-id="${c.id}" class="${c.id === s.companion ? 'on' : ''}" ${c.comingSoon ? 'disabled title="Coming soon"' : ''}>
      ${c.image ? `<img src="${c.image}" width="62" height="62" alt="" style="display:block;margin:0 auto">` : companionSVG(c.id, { size: 62, idle: false })}
      <b>${c.name}</b><small>${c.comingSoon ? 'Coming soon' : c.sport}</small></button>`).join('');
  $('pickCompanion').querySelectorAll('button:not([disabled])').forEach((b) =>
    b.onclick = () => patch({ companion: b.dataset.id }));

  $('pickPersonality').innerHTML = personalities.map((p) => {
    const locked = !allowed.includes(p.id);
    return `<button data-id="${p.id}" class="${p.id === s.personality ? 'on' : ''}" ${locked ? 'disabled' : ''}>
      <b>${p.label}</b><small>${locked ? '\u{1F512} Not for this age' : p.blurb}</small></button>`;
  }).join('');
  $('pickPersonality').querySelectorAll('button').forEach((b) =>
    b.onclick = () => patch({ personality: b.dataset.id }));
  $('personalityNote').textContent = s.strictMode
    ? 'Safe mode is on, so your companion keeps everything clean and encouraging no matter which personality you pick.'
    : 'Your companion keeps the same coaching accuracy in every personality. Only the delivery changes.';

  $('selLang').innerHTML = languages.map((l) =>
    `<option value="${l.id}" ${l.id === s.language ? 'selected' : ''}>${l.flag} ${l.native}</option>`).join('');
  $('selLang').onchange = (e) => patch({ language: e.target.value });

  const vlist = S.config.voices || [];
  $('selVoice').innerHTML = vlist.length
    ? vlist.map((v) => `<option value="${esc(v.id)}" ${v.id === s.voice ? 'selected' : ''}>${esc(v.label)} &middot; ${esc(v.blurb)}</option>`).join('')
    : '<option>Default voice</option>';
  $('selVoice').onchange = async (e) => {
    await patch({ voice: e.target.value });
    // Say something in it, so picking a voice is a decision you can hear.
    speak('This is how I sound. Lets get to work.');
  };

  // An age nobody chose is not an age (worker/visibility.js ageOf): until one
  // is chosen the picker says so, instead of showing the adult default as if
  // somebody had picked it, which also left an adult no way to choose adult.
  const ageChosen = s.ageBandChosen === true;
  $('selAge').innerHTML = (ageChosen ? '' : '<option value="" selected disabled>Choose an age range</option>') + ageBands.map((a) =>
    `<option value="${a.id}" ${ageChosen && a.id === s.ageBand ? 'selected' : ''}>${a.label}</option>`).join('');
  $('selAge').onchange = (e) => { if (e.target.value) patch({ ageBand: e.target.value }); };

  $('selMusicMode').value = s.musicMode || 'duck';
  $('swVoice').className = `sw ${s.voiceEnabled ? 'on' : ''}`;
  $('swNarrate').className = `sw ${s.autoNarrate ? 'on' : ''}`;
  $('swRoom').className = `sw ${ambience.level !== 'off' ? 'on' : ''}`;
  $('swStrict').className = `sw ${s.strictMode ? 'on' : ''}`;
  $('pinNote').textContent = s.pinSet
    ? 'PIN is set. Changing age, personality or safe mode requires it.'
    : 'Set a PIN to lock age, personality and safe mode.';
}

async function patch(body) {
  try {
    if (S.settings.pinSet && ['ageBand', 'strictMode', 'personality'].some((k) => k in body)) {
      const pin = await ui.prompt({ title: 'Parent PIN required', body: 'Age, personality and safe mode are locked.', type: 'password', placeholder: '4 digits', confirmText: 'Unlock' });
      if (!pin) return;
      body.pin = pin;
    }
    S.settings = await api('/api/settings', { method: 'PATCH', body: JSON.stringify(body) });
    S.config = await api('/api/config');
    renderSettings();
    if ('voice' in body) agent?.context({ voice: S.settings.voice });
    // An open conversation's room sound follows the companion and her voice.
    if (ambience.running && ('companion' in body || 'voice' in body)) { ambience.setVoice(S.settings.voice); ambience.start(S.settings.companion); }
    if ('companion' in body) {
      buddy?.swap(S.settings.companion);
      watcher?.swap(S.settings.companion);
      gateBall?.swap(S.settings.companion);
      talkBall?.swap(S.settings.companion);
      toast(`${companionById(S.settings.companion).name} is your companion now.`);
    }
  } catch (err) { toast(err.message, true); }
}

/* ------------------------------------------------------------------- talk */

function companionState(state) {
  [buddy, watcher, gateBall, talkBall, roomBall, miniBall].forEach((b) => b?.setState?.(state));
  $('roomMini')?.classList.toggle('talking', state === 'talking');
}
let roomBall = null, miniBall = null;

/** `direct`: straight to her brain, past the page's own handling (the film room's moment question). */
async function talk({ direct = false } = {}) {
  const input = $('talkIn');
  const message = input.value.trim();
  if (!message) return;
  input.value = '';
  transcribe('you', message);
  if (S.view === 'train') post(message, 'you', 'You');
  if (!direct && await pageHandles(message)) return;
  // On the voice agent the brain turn runs there, and her answer comes back as its events
  // (the transcript, runMeta, one voice), exactly like a spoken turn.
  if (agentLive()) { companionState('thinking'); agent.text(message, agentContext()); return null; }
  return brainTurn(message);
}

/**
 * What the page tells the brain about this moment, on either lane: training or not, the screen, what
 * the camera sees, the film on screen and where it is, and the last shot's scores. Numbers and flags
 * only; worker/api.js turns them into one line each.
 */
function pageContext() {
  const v = $('roomVideo');
  const film = roomClip && !$('roomScreen').hidden ? {
    playing: !v.paused, at: Math.round(v.currentTime || 0),
    of: Math.round(Number.isFinite(v.duration) ? v.duration : (roomClip.duration || 0)), shot: currentShot()?.index ?? null,
  } : undefined;
  const last = S.running ? S.shots.at(-1) : null;
  const lastShot = last ? { index: last.index, overall: last.overall, elbow: last.scores?.elbow, release: last.scores?.release,
    follow: last.scores?.follow, made: last.made ?? null } : undefined;
  return { training: Boolean(S.running), view: S.view, seen: S.running ? cameraSees() : undefined, film, lastShot };
}
/** The agent also needs to know when a run keeps the conversation open through long quiet stretches. */
const agentContext = () => { let keepAlive = false; try { keepAlive = Boolean(game.wantsVoice?.()); } catch {} return { ...pageContext(), keepAlive }; };
let agentTick = null;

/**
 * Things the page does itself, before any brain: the film, and the film room's own words. True (or a
 * promise of true, while the film opens) when it handled the message. The browser lane's talk() and
 * the voice agent's gate (agentTurn) both ask here first, so a sentence does the same thing on either lane.
 */
// "another / a different film", or "my last / latest / previous film": a film, not a shot in this one
// ("show me the last shot in this clip" stays in this film).
const ANOTHER_FILM = /\b(?:another|different|other|older|earlier)\b|\b(?:yesterday|today)'?s?\b|\b(?:my|the) (?:last|latest|newest|previous|most recent) (?:films?|clips?|videos?|sessions?|recordings?|tapes?)\b/i;
function pageHandles(message) {
  // Film is opened here, on the page, by her, from any screen. In her room
  // with no film up yet a bare "play it" counts; with one playing, "play" is
  // the resume below. During a live session the camera is busy recording.
  // With a film already up, only a request for another one changes it: "what do you see in this
  // clip" is about the one on screen (2026-10-10: such sentences reloaded the newest film from 0:00).
  const filmUp = S.view === 'companion' && !$('roomScreen').hidden;
  if (filmUp ? ANOTHER_FILM.test(message) && asksForFilm(message) : asksForFilm(message, { inRoom: S.view === 'companion' })) {
    if (S.running) { const l = 'Finish the session first and I will pull the video up right after.'; transcribe('coach', l); speak(l, { onStart: () => companionState('talking'), onEnd: () => companionState('idle') }); return true; }
    return openFilm(message).then(() => true);
  }
  // In the companion room, plain playback words control the video directly.
  if (S.view === 'companion' && !$('roomScreen').hidden) {
    const v = $('roomVideo'); const w = message.toLowerCase();
    if (/^(pause|stop|hold up|wait)\b/.test(w)) { v.pause(); return true; }
    if (/^(play|go|resume|keep going)\b/.test(w)) { v.play(); return true; }
    // Shots by number or by place: "shot three", "go to the last shot", "next".
    const num = /\b(?:shot|number|rep)\s+(\d{1,2}|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve)\b|\b(first|last)\s+(?:shot|rep|one)\b/.exec(w);
    if (num) { const n = num[1] ? (NUM_WORDS[num[1]] ?? Number(num[1])) : NUM_WORDS[num[2]]; seekShot(n); return true; }
    // "What do you see here?" pauses the film and asks about this exact moment.
    if (/\b(what (do|did|can) you see|what('| i)?s (wrong|off|happening)|how (can|do) i (be|get|do) better|help me here|what should i (fix|change|work on)|look at (this|that)|see (this|that)\??|talk me through (this|that))\b/.test(w)) { askAboutMoment(); return true; }
    if (/^(next|skip)\b/.test(w) && roomMarks.length) { const c = currentShot(); seekShot(c ? Math.min(c.index + 1, roomMarks.length) : 1); return true; }
    if (/^(previous|back one|last one|again|run it back)\b/.test(w) && roomMarks.length) { const c = currentShot(); seekShot(c ? Math.max(c.index - (v.currentTime - c.t > 4 ? 0 : 1), 1) : 1); return true; }
    if (/\b(best|worst) (shot|one|rep)\b/.test(w) && roomMarks.length) { const sorted = [...roomMarks].sort((a, b) => a.shot.overall - b.shot.overall); seekShot((/best/.test(w) ? sorted[sorted.length - 1] : sorted[0]).index); return true; }
    if (/(back|rewind)/.test(w)) { v.currentTime = Math.max(0, v.currentTime - 10); return true; }
    const note = /^(?:note|write down|write that down|remember|jot down|note that|note this)[:,]?\s*(.*)$/.exec(message.trim());
    if (note) { roomNote(note[1] || `Look at this moment again.`); return true; }
    if (/\b(plan|next session|wrap (it )?up|what do i work on)\b/.test(w)) { nextSessionPlan(); return true; }
  }
  return false;
}

/** The browser lane's turn: /api/chat streamed, her voice sentence by sentence. */
async function brainTurn(message) {
  companionState('thinking');
  // The conversation loop waits on this: it may not reopen the microphone
  // until the last of her reply has actually played, or she hears herself.
  let finished; const spoken = new Promise((r) => { finished = r; });
  let failure = null;
  const voice = speakStream({
    onStart: () => companionState('talking'),
    onEnd: () => { companionState('idle'); finished(); },
  });
  // Her voice ends as soon as every word is out (the server sends 'said' before the meta call and the save).
  let voiceEnded = false; const endVoice = () => { if (!voiceEnded) { voiceEnded = true; voice.end(); } };
  const early = earlyTurn; earlyTurn = null;
  const heardAs = spokenTurn; spokenTurn = null;
  const ack = heardAs === message ? pickAck(message) : null;
  ambience.onAck(Boolean(ack));
  if (ack) { voice.lead(ack); noteSaid(ack); }
  const line = transcribeStream('coach');
  let live = null;
  try {
    const { text, meta } = await streamApi('/api/chat?stream=1', { message, ...pageContext() }, {
      onMeta: runMeta,
      onText: (t) => { voice.push(t); line.push(t); if (S.view === 'train') live = feedFirstSentence(live, t); },
      // A reply started early for this turn (voice.js checked the words match) is adopted instead of asking again.
      pre: early?.adopt(),
      onSaid: endVoice,
    });
    endVoice();
    line.end(text);
    noteSaid(text);
    if (S.view === 'companion') setTimeout(renderThreads, 800);
  } catch (err) { endVoice(); line.end(''); toast(err.message, true); companionState('idle'); failure = err; }
  // A missed onEnd would leave a conversation waiting forever, so the wait is
  // bounded. Nothing she says runs anywhere near this long.
  await Promise.race([spoken, new Promise((r) => setTimeout(r, 60000))]);
  return failure;
}

/** What the brain decided besides words, on either lane: /api/chat's meta, or the agent's. */
function runMeta(m) {
  if (m.mood) { buddy?.setMood(m.mood); watcher?.setMood(m.mood); }
  if (m.event) { toast(`Added to your calendar: ${m.event.title}`); if (S.view === 'calendar') calendar.render(); }
  if (m.eventFailed) toast('That did not make it onto your calendar. Tell me the exact day and time.', true);
  if (m.action?.done) logged(m.action); else if (m.action) showAction(m.action);
  // Asking to end is a request, not an order: voice mishears, and ending
  // a workout by accident is worse than being asked twice.
  if (m.endSession && S.running) showAction({ local: true, id: 'end-session', risk: 'edit',
    summary: 'End your training session and write the report', run: () => endSession() });
  if (m.navigate) setTimeout(() => {
    go(m.navigate); toast(`Opened ${m.navigate} for you.`);
    // "Start a session" by voice: opening Train also turns the camera on.
    if (m.navigate === 'train' && !S.running) startCamera();
  }, 700);
}

/**
 * A spoken turn on the voice agent. The browser lane's own gate, in the same order: a card waiting
 * for a yes or no, her own voice coming back, a play called out during a live run, then the page's
 * own words (the film, the film room), and only then the brain, with what the page knows and an
 * acknowledgement to say first. What comes back tells the agent what to do with the turn.
 */
function agentTurn(said) {
  if (answeredByVoice(said)) return { go: 'skip' };
  if (isEcho(said) || (isFiller(said) && justSpoke())) return { go: 'skip' };
  try { if (game.wantsVoice?.() && game.voiceLine?.(said)) return { go: 'skip' }; } catch (err) { console.warn('voice log failed', err); }
  const message = said.trim();
  transcribe('you', message);
  $('talkIn').value = '';
  if (S.view === 'train') post(message, 'you', 'You');
  if (pageHandles(message)) return { go: 'skip' };
  companionState('thinking');
  const ack = agentAck(message);
  if (ack) noteSaid(ack);
  return { go: 'brain', hooper: agentContext(), ack };
}

/** pickAck for the agent: the same rules, but the agent keeps the acknowledgements warm itself. */
function agentAck(said) {
  if ((S.settings.language || 'en') !== 'en' || String(said || '').trim().split(/\s+/).length <= 2) return null;
  const choices = ACKS.filter((l) => l !== lastAck);
  lastAck = choices[Math.floor(Math.random() * choices.length)];
  return lastAck;
}

/**
 * True from the first word of a reply until the last clip has finished.
 *
 * It used to read the audio element, which is PAUSED between one sentence and
 * the next while the next clip is still being fetched. In that gap the mic
 * reopened, heard the companion's own voice from the laptop speaker, and sent
 * it back as a new message. That is the loop that repeated "Okay." thirty
 * times in front of the partners. speakStream now raises `replyInFlight` for
 * its whole life, gaps included.
 */
let replyInFlight = 0, lastReplyEndAt = 0;
const companionSpeaking = () =>
  replyInFlight > 0 || Boolean(agent?.speaking) || !!(audioEl && !audioEl.paused && !audioEl.ended) || !!window.speechSynthesis?.speaking;

/* ------------------------------------------------------------ the conversation
 * The microphone used to be one shot: say one thing, get one answer, and the
 * recognizer was finished. Nothing reopened it, so she appeared to stop
 * replying after the first message. This is an ongoing back and forth instead,
 * and it runs until the player ends it. voice.js holds the loop.
 */
const TALK_STATE = {
  starting: 'Starting',
  listening: 'Listening',
  thinking: 'Thinking',
  talking: 'Speaking',
};

/**
 * The companion room IS the conversation, so the floating bar and the round
 * reopen button are hidden while it is open: two ways to start the same chat,
 * one of them covering the companion's face on a phone.
 */
function paintTalkChrome() {
  const inRoom = S.view === 'companion';
  document.querySelector('.talkbar')?.classList.toggle('room-hidden', inRoom);
  $('btnTalkOpen')?.classList.toggle('room-hidden', inRoom);
  const live = conversation.live;
  const btn = $('roomTalk');
  if (btn) {
    btn.classList.toggle('live', live);
    $('roomTalkLabel').textContent = live ? 'Stop talking' : 'Start talking';
    $('roomTalkHint').textContent = live
      ? 'Listening. Say what you want, and it keeps going.'
      : 'Tap once and keep talking. It stays listening until you stop it.';
  }
  // The same control, for when the panel has been put away.
  const solo = $('roomTalkSolo');
  if (solo) {
    solo.classList.toggle('live', live);
    solo.setAttribute('aria-pressed', String(live));
    $('roomTalkSoloLabel').textContent = live ? 'Stop talking' : 'Start talking';
  }
}

/* ------------------------------------------------- the room, companion only
 * Putting the panel away leaves nothing on screen but the companion. The choice
 * is remembered, because somebody who wants the room clean wants it clean next
 * time too. A small bar stands in for the panel so there is always a way to
 * talk and always a way back.
 */
const ROOM_SOLO_KEY = 'hb-room-solo';
function roomSolo(on) {
  const room = document.querySelector('#v-companion .room');
  if (!room) return;
  room.classList.toggle('solo', on);
  const bar = $('roomSoloBar');
  if (bar) bar.hidden = !on;
  try { localStorage.setItem(ROOM_SOLO_KEY, on ? '1' : '0'); } catch {}
  paintTalkChrome();
}
const roomIsSolo = () => Boolean(document.querySelector('#v-companion .room')?.classList.contains('solo'));

function paintTalkState(state) {
  const bar = document.querySelector('.talkbar');
  const pill = $('talkState');
  const mic = $('btnMic');
  const live = state !== 'off';
  bar?.classList.toggle('live', live);
  mic?.classList.toggle('rec', state === 'listening');
  mic?.classList.toggle('on', live);
  mic?.setAttribute('aria-pressed', String(live));
  mic?.setAttribute('title', live ? 'End the conversation' : 'Start talking');
  if (pill) {
    pill.hidden = !live;
    pill.textContent = TALK_STATE[state] || '';
    pill.dataset.state = state;
  }
  $('talkIn')?.setAttribute('placeholder', live
    ? 'Listening. Talk, or type instead' + String.fromCharCode(8230)
    : 'Talk to your companion' + String.fromCharCode(8230));
  if (!live) radio?.duck(false);
  paintTalkChrome();
}

const conversation = createConversation({
  lang: () => (S.settings.language === 'en' || !S.settings.language ? 'en-US' : S.settings.language),
  // A live run keeps the microphone open through the quiet stretches between
  // plays, which is most of what a park session sounds like.
  keepAlive: () => { try { return Boolean(game.wantsVoice?.()); } catch { return false; } },
  // A room sound filler counts as speaking, so the mic never reopens over her spoken um (ambience.js).
  isSpeaking: () => companionSpeaking() || ambience.fillerPlaying,
  notify: (message, bad) => toast(message, !!bad),
  onState: (state) => {
    // Room sound (ambience.js) starts and stops with the conversation.
    if (state === 'off') ambience.stop();
    else if (!ambience.running) { ambience.setVoice(S.settings.voice); ambience.start(S.settings.companion); }
    if (state === 'thinking') ambience.onUserStopped();
    if (state === 'listening' && !agentLive()) warmAcks();
    paintTalkState(state);
    if (state === 'listening') radio?.duck(true);
    if (state !== 'off') companionState(state === 'thinking' || state === 'starting' ? 'thinking' : state);
    else companionState('idle');
  },
  // Partial words land in the box so the player can see they are being heard.
  onPartial: (text) => { ambience.onUserStarted(); if (conversation.live) $('talkIn').value = text; },
  // The live voice lane (voicertc.js) shows the conversation through these. The
  // node already ran the turn through her brain as this player, so nothing is re-sent.
  onUserText: (text) => { transcribe('you', text); $('talkIn').value = ''; },
  onBotLine: () => { const line = transcribeStream('coach'); return { push: (t) => line.push(t), end: (t) => { line.end(t); noteSaid(t); if (S.view === 'companion') setTimeout(renderThreads, 800); } }; },
  // The voice agent inside Cloudflare (voicecf.js): the page's gate and handlers for its turns.
  agent: {
    voice: () => S.settings.voice,
    onAgent: (a) => {
      agent = a;
      clearInterval(agentTick); agentTick = null;
      if (!a) return;
      a.context({ ...agentContext(), voice: S.settings.voice, acks: ACKS });
      // A run starts, the film plays on: what is on screen changes without a turn. Never counted as talk.
      agentTick = setInterval(() => { if (agentLive()) agent.context(agentContext()); }, 15000);
    },
    onTurn: (said) => agentTurn(said),
    onMeta: (m) => runMeta(m),
    onSaid: (text) => noteSaid(text),
  },
  // Start the reply on Flux's early end of turn. Only for a turn that will reach /api/chat; anything talk() answers on the page is never started early.
  prepare: (said) => {
    if ((S.settings.language || 'en') !== 'en') return null;
    if (S.view === 'companion' && !$('roomScreen').hidden) return null;
    if (asksForFilm(said, { inRoom: S.view === 'companion' && $('roomScreen').hidden })) return null;
    if (isEcho(said) || isFiller(said)) return null;
    try { if (game.wantsVoice?.()) return null; } catch {}
    const ac = new AbortController(); let used = false;
    const res = fetch('/api/chat?stream=1', { method: 'POST', signal: ac.signal, headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ message: said.trim(), training: Boolean(S.running), view: S.view, seen: S.running ? cameraSees() : undefined, speculative: true }) });
    res.catch(() => {});
    return { text: said, adopt: () => { used = true; return res; }, cancel: () => { if (!used) ac.abort(); } };
  },
  ask: async (said, early) => {
    // A card waiting for an answer takes the next thing they say.
    if (answeredByVoice(said)) return;
    // ECHO. On a laptop the mic is inches from the speaker. If what came in
    // is what the companion just said, or a piece of it, it is her own voice
    // coming back and it must not become a turn. Nor may a bare "okay" or
    // "yeah": a one word acknowledgement is not a question, and answering it
    // is how one echo turns into thirty.
    if (isEcho(said) || (isFiller(said) && justSpoke())) return;
    // During a run, a spoken play is a stat, not a question for the companion.
    try { if (game.wantsVoice?.() && game.voiceLine?.(said)) return; } catch (err) { console.warn('voice log failed', err); }
    $('talkIn').value = said;
    spokenTurn = said.trim();
    earlyTurn = early || null;
    const failed = await talk();
    earlyTurn = null;
    // Out of replies for today, or out of companion minutes (402), is the end
    // of the conversation, not a turn to retry. Without this the loop would
    // listen, get the same refusal, and go round again, which looks exactly
    // like the companion ignoring them.
    if (failed?.status === 429 || failed?.status === 402) throw Object.assign(new Error(failed.message), { code: 'rate' });
  },
});

/* ------------------------------------------------------- confirming actions
 * The companion never writes anything. It proposes, the server parks the
 * proposal and hands back a sentence and an id, and this is where a person
 * agrees. The id is the only thing that goes back, so nothing here can invent
 * a write, and a misheard "delete that" dies on this card instead of in their
 * record.
 */
let pendingAction = null;

const ACTION_REFRESH = {
  log_meal: 'nutrition', log_drink: 'nutrition', edit_last: 'nutrition', delete_last: 'nutrition',
  log_body: 'health', log_journal: 'journal', log_drill: 'skills',
};

/**
 * Something they said was logged on its own (worker/actions.js autoRun). Say
 * so, refresh whatever screen owns it, and offer one tap to undo while the
 * undo proposal is alive. Nothing to confirm: the point is that it is done.
 */
async function logged(a) {
  const what = a.summary.replace(/^Log(?: to your body record| a drink)?:?\s*/i, '');
  toastAction(`Logged: ${what}`, a.undo ? 'Undo' : null, async () => {
    try { await api('/api/actions/run', { method: 'POST', body: JSON.stringify({ id: a.undo }) }); toast('Undone.'); }
    catch (e) { toast(e.message, true); }
    const v = ACTION_REFRESH[a.action]; if (v && S.view === v) { try { await refreshView(v); } catch {} }
    try { renderJournal(); } catch {}
  });
  const view = ACTION_REFRESH[a.action];
  if (view && S.view === view) { try { await refreshView(view); } catch {} }
  try { renderJournal(); } catch {}
}

/** A toast that can carry one button. */
function toastAction(msg, label, onTap) {
  const el = $('toast');
  el.textContent = msg;
  if (label) {
    const b = document.createElement('button'); b.textContent = label; b.className = 'toast-btn';
    b.onclick = () => { clearTimeout(toast.t); el.className = 'toast'; onTap?.(); };
    el.append(' ', b);
  }
  el.className = 'toast show';
  clearTimeout(toast.t);
  toast.t = setTimeout(() => (el.className = 'toast'), label ? 7000 : 3400);
}

function showAction(a) {
  // The server refuses some asks outright, for example deleting a meal when
  // there is not one. That is a sentence, not a card.
  if (a?.error) { toast(a.error, true); return; }
  if (!a?.id) return;
  pendingAction = a;
  $('actRisk').textContent = a.local ? 'Confirm' : a.risk === 'delete' ? 'Delete' : a.risk === 'edit' ? 'Change' : 'Log';
  $('actRisk').dataset.risk = a.risk || 'log';
  $('actText').textContent = a.summary;
  $('actYes').textContent = a.risk === 'delete' ? 'Yes, delete it' : 'Yes, do it';
  $('actCard').hidden = false;
}

function hideAction() { pendingAction = null; $('actCard').hidden = true; }

async function answerAction(yes) {
  const a = pendingAction;
  if (!a) return false;
  hideAction();
  // Something the page does itself, with no server proposal behind it.
  if (a.local) {
    if (yes) { try { await a.run(); } catch (e) { toast(e.message, true); } }
    else toast('Okay. Keep going.');
    return true;
  }
  try {
    const r = await api(`/api/actions/${yes ? 'run' : 'drop'}`, { method: 'POST', body: JSON.stringify({ id: a.id }) });
    if (!yes) { toast('Left it alone.'); return true; }
    toast(a.summary.replace(/^(Log|Delete|Change)\b/, (m) => ({ Log: 'Logged', Delete: 'Deleted', Change: 'Changed' }[m])));
    // Whatever screen owns that record needs to redraw, and the journal shows
    // most of them, so refresh it too.
    const view = ACTION_REFRESH[a.action];
    if (view && S.view === view) { try { await refreshView(view); } catch {} }
    try { renderJournal(); } catch {}
    return true;
  } catch (e) { toast(e.message, true); return true; }
}

/** A view that can redraw itself after the companion changed something in it. */
async function refreshView(view) {
  const mod = { nutrition, health, skills, journal: null }[view];
  if (mod?.render) await mod.render();
}

/**
 * Hands free confirming. While a conversation is running, "yes" and "no" are
 * answers to the card in front of them, not new things to say to the companion.
 * Without this the only way to agree is to reach for the phone, which defeats
 * the point of asking out loud in the first place.
 */
const YES = /^(yes|yeah|yep|yup|sure|do it|go ahead|confirm|correct|that is right|thats right|okay|ok)\b/i;
const NO = /^(no|nope|nah|cancel|stop|do not|don't|leave it|forget it)\b/i;
function answeredByVoice(said) {
  if (!pendingAction) return false;
  const t = String(said || '').trim();
  if (YES.test(t)) { answerAction(true); return true; }
  if (NO.test(t)) { answerAction(false); return true; }
  return false;
}

/**
 * Show or hide the talk bar. Focusing the input is optional because on a phone
 * focus throws the keyboard up over the screen, which is the wrong thing when
 * the player pressed a button to start TALKING.
 */
function showTalk(on, { focus = true } = {}) {
  document.querySelector('.talkbar').classList.toggle('hidden', !on);
  $('btnTalkOpen').classList.toggle('on', !on);
  try { localStorage.setItem('hb-talk', on ? 'on' : 'off'); } catch {}
  if (on && focus && window.innerWidth > 900) $('talkIn').focus();
}

/* --------------------------------------------------------- echo and filler
 * Everything the companion says is remembered for a short while so that if it
 * comes back through the microphone it is recognised as hers. Comparison is
 * on words, lower cased, punctuation stripped, and it counts as an echo when
 * most of what was heard is contained in what she said.
 */
const recentlySaid = [];
const norm = (t) => String(t || '').toLowerCase().replace(/[^a-z0-9 ]+/g, ' ').replace(/\s+/g, ' ').trim();
function noteSaid(text) {
  const n = norm(text); if (!n) return;
  recentlySaid.push({ n, at: Date.now() });
  while (recentlySaid.length > 12) recentlySaid.shift();
}
/** Her last reply ended in the last three seconds, or is still going. */
const justSpoke = () => companionSpeaking() || Date.now() - lastReplyEndAt < 3000;
function isEcho(heard) {
  const h = norm(heard); if (!h) return true;
  const cut = Date.now() - 20000;
  const words = h.split(' ');
  for (const r of recentlySaid) {
    if (r.at < cut) continue;
    // A phrase of hers coming straight back is an echo. One or two words that
    // she also used only count right after she said them: "shoulders" a
    // minute later is the player, not the speaker.
    if (r.n.includes(h) && (words.length >= 3 || justSpoke())) return true;
    const hit = words.filter((w) => w.length > 2 && r.n.includes(w)).length;
    if (words.length >= 3 && hit / words.length >= 0.7) return true;
  }
  return false;
}
// A bare acknowledgement heard right after she spoke is her own voice, or a
// nod, and neither needs an answer. Ten seconds later "yeah" is the player.
const FILLER = /^(ok|okay|yeah|yep|yup|mm|mhm|uh huh|hmm|right|cool|nice|alright|sure|word|bet|facts|no cap)[.!?\s]*$/i;
const isFiller = (t) => FILLER.test(String(t || '').trim());

/** The mic button starts and ends the whole conversation, not one sentence. */
/** Wake the model while they are still talking, so their first turn is not the cold one. */
function warmCompanion() { fetch('/api/warm', { method: 'POST' }).catch(() => {}); }

async function listen() {
  if (conversation.live) { stopSpeaking(); conversation.stop(); return; }
  warmCompanion();
  // Opening the bar first matters on a phone: the button that starts a
  // conversation should not be the one that is hidden behind the reopen pill.
  showTalk(true, { focus: false });
  await conversation.start();
}

/* ------------------------------------------------------- the companion, early
 * This used to mount after two sequential API calls, so the face of the app
 * arrived late on a slow connection and never arrived at all if either call
 * failed. Mount it immediately from the companion we remembered last time,
 * then swap once the real settings land. Nothing here touches the network.
 */
const REMEMBERED = (() => { try { return localStorage.getItem('hb-companion') || 'basketball'; } catch { return 'basketball'; } })();
function mountCompanions(id) {
  const spots = [[heroSpot(), 180], ['watcher', 104], ['gateBall', 130], ['talkBall', 34], ['reopenBall', 38]];
  const made = {};
  for (const [key, size] of spots) {
    const el = typeof key === 'string' ? $(key) : key;
    if (!el) continue;
    made[typeof key === 'string' ? key : 'heroBall'] = mountCompanion(el, id, { size });
  }
  return made;
}
const heroSpot = () => $('heroBall');
(function companionFirst() {
  try {
    const m = mountCompanions(REMEMBERED);
    buddy = m.heroBall || null; watcher = m.watcher || null;
    gateBall = m.gateBall || null; talkBall = m.talkBall || null;
  } catch (e) { console.warn('companion could not mount early', e); }
})();

/* ---------------------------------------------------------------- the rail
 * Collapse and expand, and the labels. This is deliberately wired at module
 * load rather than inside boot(): if the API is unreachable the player should
 * still be able to move around the app instead of facing a dead screen.
 * The open/closed state is written to localStorage and applied by /js/theme.js
 * before first paint, so the menu never flashes narrow and jumps wide.
 */
const railOpen = () => document.documentElement.dataset.rail === 'open';
function setRail(open) {
  const root = document.documentElement;
  if (open) root.dataset.rail = 'open'; else delete root.dataset.rail;
  const label = open ? 'Collapse the menu' : 'Expand the menu';
  const btn = $('railToggle');
  if (btn) { btn.setAttribute('aria-expanded', String(open)); btn.title = label; btn.querySelector('.tip').textContent = label; }
  // Remembered on a laptop only: a phone's drawer is for this visit.
  if (!onPhone()) { try { localStorage.setItem('hb-rail', open ? 'open' : 'closed'); } catch {} }
}
const onPhone = () => matchMedia('(max-width: 900px)').matches;
/* Sections remember whether they are open, per section, across visits. */
const SECKEY = 'hb-rail-secs';
const readSecs = () => { try { return new Set(JSON.parse(localStorage.getItem(SECKEY) || '[]')); } catch { return new Set(); } };
function setSection(sec, open) {
  const el = document.querySelector(`.rail-sec[data-sec="${sec}"]`);
  if (!el) return;
  el.classList.toggle('closed', !open);
  el.querySelector('.rail-group')?.setAttribute('aria-expanded', String(open));
  const closed = readSecs();
  if (open) closed.delete(sec); else closed.add(sec);
  try { localStorage.setItem(SECKEY, JSON.stringify([...closed])); } catch {}
}

(function wireChrome() {
  // Swap every data-icon holder in the markup for its inline svg.
  icons.paint(document);
  setRail(railOpen());
  // Restore each section, then wire its heading.
  const closed = readSecs();
  document.querySelectorAll('.rail-sec').forEach((el) => {
    const sec = el.dataset.sec;
    const head = el.querySelector('.rail-group');
    const count = el.querySelectorAll('button[data-view]').length;
    head?.insertAdjacentHTML('beforeend', `<b>${count}</b>`);
    el.classList.toggle('closed', closed.has(sec));
    head?.setAttribute('aria-expanded', String(!closed.has(sec)));
    if (head) head.onclick = () => setSection(sec, el.classList.contains('closed'));
  });
  // Navigation is bound here too, for the same reason: a failed API call must
  // not leave the player stuck on whichever screen happened to be open.
  document.querySelectorAll('.rail button[data-view]').forEach((b) =>
    b.onclick = () => {
      go(b.dataset.view);
      // On a phone the expanded rail covers the view it just opened.
      if (railOpen() && window.innerWidth <= 900) setRail(false);
    });
  document.querySelectorAll('[data-go]').forEach((b) =>
    b.onclick = () => go(b.dataset.go));
  const btn = $('railToggle');
  if (btn) btn.onclick = () => setRail(!railOpen());
  // A collapsed label is position:fixed so the scrolling rail cannot clip it,
  // which means its vertical position has to come from the button it belongs to.
  document.querySelectorAll('.rail button .tip').forEach((tip) => {
    const owner = tip.parentElement;
    owner.addEventListener('mouseenter', () => {
      if (railOpen()) return;
      const r = owner.getBoundingClientRect();
      tip.style.top = `${Math.round(r.top + r.height / 2)}px`;
    });
  });
})();

/** New badges from a finished session: tell them, and refresh the Compete card. */
function social_celebrate(earned) {
  earned.slice(0, 3).forEach((b, i) => setTimeout(() => toast(`Badge earned: ${b.name}`), 900 * (i + 1)));
  social.noteEarned?.(earned);
}

/* ------------------------------------------------------------------- boot */

(async function boot() {
  // In parallel, not one after the other, and survivable: the chrome and the
  // companion are already up, so a failed request degrades the app instead of
  // ending it.
  const [settings, config] = await Promise.all([
    api('/api/settings').catch((e) => { console.warn('settings failed', e.message); return null; }),
    api('/api/config').catch((e) => { console.warn('config failed', e.message); return null; }),
  ]);
  if (!settings || !config) toast('Working offline for now. Some things will be out of date.', true);
  S.settings = settings || { ...S.settings, companion: REMEMBERED, displayName: 'Hooper', voiceEnabled: true, autoNarrate: true, musicMode: 'duck', language: 'en' };
  S.config = config || { companions: [], personalities: [], languages: [], ageBands: [], allowed: [] };

  // Swap only if the remembered face was not the right one.
  if (S.settings.companion && S.settings.companion !== REMEMBERED) {
    [buddy, watcher, gateBall, talkBall].forEach((b) => b?.swap?.(S.settings.companion));
  }
  try { localStorage.setItem('hb-companion', S.settings.companion || 'basketball'); } catch {}


  $('btnStart').onclick = startCamera;
  $('btnAsk').onclick = () => askCoach(false);
  $('btnEnd').onclick = endSession;
  // The browser's own "leave site?" prompt, only while a session is running.
  // Closing the tab mid workout throws away the recording and the report.
  window.addEventListener('beforeunload', (e) => {
    if (!S.running) return;
    e.preventDefault();
    e.returnValue = '';
  });
  $('btnMake').onclick = () => mark(true);
  $('btnMiss').onclick = () => mark(false);
  $('btnSim').onclick = simulate;
  $('btnTrace').onclick = saveTrace;
  $('btnVaultRefresh').onclick = renderVault;
  /* companion room */
  $('roomPause').onclick = () => { const v = $('roomVideo'); v.paused ? v.play() : v.pause(); };
  $('roomSound').onclick = () => { const v = $('roomVideo'); v.muted = false; v.play().catch(() => {}); $('roomSound').hidden = true; };
  $('roomVideo').onplay = $('roomVideo').onpause = () => { $('roomPause').textContent = $('roomVideo').paused ? 'Play' : 'Pause'; };
  $('roomBack').onclick = () => { $('roomVideo').currentTime = Math.max(0, $('roomVideo').currentTime - 10); };
  $('overlay').addEventListener('click', (ev) => { if (S.running) markRim(ev); });
  $('btnFlip').onclick = flipCamera;
  document.querySelectorAll('.tabbar button[data-view]').forEach((b) => { b.onclick = () => { if (railOpen()) setRail(false); go(b.dataset.view); }; });
  $('tabMore').onclick = () => $('railToggle')?.click();
  // The dimmed page around the phone drawer is only a shadow, so a tap on it
  // lands on the page: close the drawer first and let the tap through.
  document.addEventListener('click', (ev) => { if (railOpen() && onPhone() && !ev.target.closest('.rail, #tabMore')) setRail(false); }, true);
  // The app shell installs itself on the device: instant second open, works as
  // a home screen app on iOS and Android, engine bundle kept locally.
  if ('serviceWorker' in navigator && (location.protocol === 'https:' || /^(localhost|127\.0\.0\.1)$/.test(location.hostname))) navigator.serviceWorker.register('/sw.js').catch((e) => console.warn('sw', e?.message));
  // Live mode's buttons (phone training) are the page's own buttons, in reach over the camera.
  for (const [live, real] of [['liveMake', 'btnMake'], ['liveMiss', 'btnMiss'], ['liveAsk', 'btnAsk'], ['liveEnd', 'btnEnd']]) {
    $(live).onclick = () => $(real).click();
    const sync = () => { $(live).disabled = $(real).disabled; };
    new MutationObserver(sync).observe($(real), { attributes: true, attributeFilter: ['disabled'] });
    sync();
  }
  // A stream that changes size (a camera flip, the far step up to 4K) reshapes the stage and the
  // detector's idea of a pixel.
  $('cam').addEventListener('resize', () => { setStageShape(); if (S.running && $('cam').videoWidth) detector.setFrameWidth($('cam').videoWidth); });
  $('roomAsk').onclick = askAboutMoment;
  $('roomPlan').onclick = nextSessionPlan;
  $('roomVideo').ontimeupdate = roomTick;
  $('roomVideo').onended = () => { if (roomMarks.length) { const l = 'That is the film. Say plan and I will tell you what to work on next time.'; transcribe('coach', l); speak(l); } };
  $('roomClose').onclick = () => { $('roomVideo').pause(); $('roomScreen').hidden = true; document.querySelector('.room').classList.remove('watching'); roomClip = null; roomSession = null; roomMarks = []; roomSaid = new Set(); };
  /* journal */
  paintPrompts();
  restoreDraft();
  watchPad();
  $('jText').oninput = () => { growPad(); updateCount(); queueDraft(); };
  $('jMood').onchange = queueDraft;
  $('jMore').onclick = () => { jShown += 25; renderJournal(); };
  $('jSave').onclick = async () => {
    const text = $('jText').value.trim();
    const attachments = media.pendingAttachments();
    if (!text && !attachments.length) return toast('Write something first, or add a photo, a voice note or a clip.');
    const btn = $('jSave'); btn.disabled = true;
    try {
      await api('/api/journal', { method: 'POST', body: JSON.stringify({ text: text || 'Attachment', mood: $('jMood').value, media: attachments }) });
      $('jText').value = ''; $('jMood').value = ''; media.clearPending();
      clearDraft(); growPad(); updateCount();
      toast('Saved to your journal.'); renderJournal();
    } catch (e) { toast(e.message, true); }
    finally { btn.disabled = false; }
  };
  // Same shape as the entry save above. A failed log used to throw into
  // nothing: no message, numbers still sitting in the boxes, no way to tell it
  // had not saved. Keep what they typed and say so.
  $('bSave').onclick = async () => {
    const btn = $('bSave'); btn.disabled = true;
    try {
      await api('/api/body', { method: 'POST', body: JSON.stringify({ weightKg: $('bW').value, heightCm: $('bH').value, waterCups: $('bWater').value, sleepHours: $('bSleep').value, restingHr: $('bHr').value, note: $('bNote').value }) });
      ['bW', 'bWater', 'bSleep', 'bHr', 'bNote'].forEach((id) => { $(id).value = ''; });
      toast('Body log saved.'); renderJournal();
    } catch (e) { toast(e.message, true); }
    finally { btn.disabled = false; }
  };
  /* music tab */
  $('mPlay').onclick = () => radio.toggle().then(paintNow).catch((e) => toast(`Music: ${e.message}`, true));
  $('mStop').onclick = () => { radio.stop(); paintNow(); };
  $('mNext').onclick = () => radio.next().then(afterMusicChange).catch((e) => toast(`Music: ${e.message}`, true));
  $('mBack').onclick = () => radio.back(15);
  $('mShuffle').onclick = () => radio.setShuffle(!radio.shuffle);
  $('mRepeat').onclick = () => radio.cycleRepeat();
  $('mSave').onclick = () => saveToPlaylist(radio.current);
  $('mMode').onchange = (e) => patch({ musicMode: e.target.value });
  $('npSeek').oninput = (e) => { if (radio.duration) radio.seek((e.target.value / 1000) * radio.duration); };
  $('swRecord').onclick = () => patch({ recordSessions: !S.settings.recordSessions }).then(renderVault);
  $('clipClose').onclick = () => { $('clipModal').classList.remove('open'); $('clipVideo').pause(); };
  $('clipModal').onclick = (e) => { if (e.target === $('clipModal')) { $('clipModal').classList.remove('open'); $('clipVideo').pause(); } };

  /* SoulTown Music radio */
  radio = createRadio({
    onTime: (pos, dur) => { if (S.view !== 'music' || !dur) return; $('npSeek').value = Math.round((pos / dur) * 1000); $('npTime').textContent = `${vault.fmtDur(pos)} / ${vault.fmtDur(dur)}`; },
    onTrack: (t) => {
      afterMusicChange();
      $('radioTitle').textContent = t.title || 'SoulTown Music';
      $('radioSub').textContent = t.artist || t.genre || 'SoulTown Music';
      const art = $('radioArt'); if (t.art) { art.src = t.art; art.hidden = false; } else art.hidden = true;
      if (t.accent_color) $('radio').style.setProperty('--accent', t.accent_color);
      $('radioLike').classList.remove('on'); $('radioLike').setAttribute('aria-pressed', 'false');
    },
    onState: (st) => {
      if (S.view === 'music') paintNow();
      $('radioPlay').innerHTML = icons.icon(st === 'playing' ? 'pause' : 'play', 20);
      $('radio').classList.toggle('live', st === 'playing');
      if (st !== 'playing') $('radioSub').textContent = radio.current ? 'Paused' : 'Tap to open the station';
    },
    onMode: () => paintModes(),
  });
  // Shuffle and repeat for the corner player. The row is built here rather than
  // in app.html because the radio markup belongs to the shell, not the music tab.
  const radioModes = document.createElement('div');
  radioModes.className = 'radio-save';
  radioModes.innerHTML = `
    <button class="rsave" id="radioShuffle" aria-pressed="false" title="Shuffle"><i>${icons.icon('shuffle', 15)}</i> Shuffle</button>
    <button class="rsave" id="radioRepeat" aria-pressed="false" title="Repeat"><i>${icons.icon('repeat', 15)}</i> <span id="radioRepeatLab">Repeat off</span></button>`;
  $('radioBody').insertBefore(radioModes, $('radioBody').querySelector('.radio-save'));
  $('radioShuffle').onclick = () => radio.setShuffle(!radio.shuffle);
  $('radioRepeat').onclick = () => radio.cycleRepeat();
  paintModes();
  radio.setVolume(S.settings.musicVolume ?? 0.8);
  $('radioVol').value = Math.round((S.settings.musicVolume ?? 0.8) * 100);
  // The catalog (85 KB) loads when the player first opens or plays, not on every app open.
  const countTracks = () => radio.load().then(() => { $('radioCount').textContent = `${radio.count} tracks`; }).catch(() => { $('radioCount').textContent = 'station offline'; });
  // Collapsed it is a small button. Opening it reveals the whole player.
  const radioOpen = (open) => {
    if (open) countTracks();
    $('radio').classList.toggle('min', !open);
    $('radioHead').setAttribute('aria-expanded', String(open));
  };
  $('radioHead').onclick = (e) => {
    if (e.target.id === 'radioClose') return;                 // the x collapses it, handled below
    radioOpen($('radio').classList.contains('min'));
  };
  // The x dismisses the player for good and remembers it, so it does not come
  // back on the next visit. Settings has "Open player" to bring it back on
  // purpose, which is the only way back: that is what "closed" should mean.
  const hideRadio = (gone) => {
    $('radio').classList.toggle('gone', gone);
    try { localStorage.setItem('hb-radio', gone ? 'off' : 'on'); } catch {}
  };
  $('radioClose').onclick = (e) => { e.stopPropagation(); hideRadio(true); toast('Music player closed. Open it again from Settings.'); };
  try { if (localStorage.getItem('hb-radio') === 'off') hideRadio(true); } catch {}
  $('radioPlay').onclick = () => radio.toggle().catch((e) => toast(`Music: ${e.message}`, true));
  $('radioNext').onclick = () => radio.next().then(afterMusicChange).catch((e) => toast(`Music: ${e.message}`, true));
  $('radioBack').onclick = () => radio.back(15);
  $('radioStop').onclick = () => { radio.stop(); paintNow(); };
  // Like is a playlist called Liked, so it uses the storage that already exists
  // rather than inventing a second idea of a favourite.
  $('radioLike').onclick = async () => {
    const t = radio.current;
    if (!t) return toast('Nothing playing yet.');
    try {
      await api('/api/playlists', { method: 'POST', body: JSON.stringify({ name: 'Liked', track: { id: t.id, title: t.title, art: t.art, audio_url: t.audio_url } }) });
      $('radioLike').classList.add('on'); $('radioLike').setAttribute('aria-pressed', 'true');
      toast(`Liked ${t.title || 'this track'}.`);
    } catch (e) { toast(e.message, true); }
  };
  $('radioAdd').onclick = () => saveToPlaylist(radio.current);
  $('radioVol').oninput = (e) => radio.setVolume(e.target.value / 100);
  $('radioVol').onchange = (e) => patch({ musicVolume: e.target.value / 100 });
  const paintMode = () => ['duck', 'quiet'].forEach((m) => $(m === 'duck' ? 'modeDuck' : 'modeQuiet').classList.toggle('on', S.settings.musicMode === m));
  paintMode();
  $('modeDuck').onclick = () => patch({ musicMode: 'duck' }).then(paintMode);
  $('modeQuiet').onclick = () => patch({ musicMode: 'quiet' }).then(paintMode);
  $('btnRadioOpen').onclick = () => { hideRadio(false); radioOpen(true); };
  $('selMusicMode').onchange = (e) => patch({ musicMode: e.target.value }).then(paintMode);
  $('btnTalk').onclick = talk;
  $('btnTalkClose').onclick = () => { conversation.stop(); showTalk(false); };
  $('btnTalkOpen').onclick = () => showTalk(true);
  try { if (localStorage.getItem('hb-talk') === 'off') showTalk(false); } catch {}
  $('btnMic').onclick = listen;
  // The room's own controls. The big button is the whole point of the panel.
  $('roomTalk').onclick = async () => {
    if (conversation.live) { stopSpeaking(); conversation.stop(); } else { warmCompanion(); await conversation.start(); }
    paintTalkChrome();
  };
  $('roomFull').onclick = () => roomFull(!roomIsFull());
  $('roomFullSolo').onclick = () => roomFull(!roomIsFull());
  $('roomPanelHide').onclick = () => roomSolo(true);
  $('roomPanelShow').onclick = () => roomSolo(false);
  $('roomTalkSolo').onclick = () => $('roomTalk').click();
  try { if (localStorage.getItem(ROOM_SOLO_KEY) === '1') roomSolo(true); } catch {}
  $('actYes').onclick = () => answerAction(true);
  $('actNo').onclick = () => answerAction(false);
  // Escape leaves full screen before it does anything else.
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    if (roomIsFull()) { roomFull(false); return; }
    if (pendingAction) answerAction(false);
  });
  $('talkIn').onkeydown = (e) => { if (e.key === 'Enter') talk(); };
  $('talkIn').addEventListener('focus', warmCompanion, { once: true });
  $('btnReheat').onclick = () => brainState?.reheat();
  $('mClose').onclick = () => $('modal').classList.remove('open');
  $('modal').onclick = (e) => { if (e.target === $('modal')) $('modal').classList.remove('open'); };
  $('dockHead').onclick = () => $('dock').classList.toggle('min');
  $('btnOut').onclick = () => (location.href = '/');
  const SWITCHES = { swVoice: 'voiceEnabled', swNarrate: 'autoNarrate', swStrict: 'strictMode' };
  Object.entries(SWITCHES).forEach(([id, key]) => {
    $(id).onclick = () => patch({ [key]: !S.settings[key] });
  });
  // Room sound: subtle by default, off is off. Remembered on this device (ambience.js).
  $('swRoom').onclick = () => { ambience.setLevel(ambience.level === 'off' ? 'subtle' : 'off'); $('swRoom').className = `sw ${ambience.level !== 'off' ? 'on' : ''}`; };
  $('btnPin').onclick = async () => {
    const v = $('inPin').value.trim();
    if (!v) return toast('Enter a PIN first.', true);
    const current = S.settings.pinSet
      ? await ui.prompt({ title: 'Current PIN', body: 'Enter the PIN you set before, so only a parent can change it.', type: 'password', confirmText: 'Change PIN' })
      : undefined;
    if (S.settings.pinSet && !current) return;
    await patch({ parentPin: v, pin: current });
    $('inPin').value = '';
  };
  // Delete this account and everything in it (DELETE /api/me, worker/erase.js).
  $('btnDeleteAccount').onclick = async () => {
    const typed = await ui.prompt({ title: 'Delete this account?', body: 'Every session, note, meal, clip and minute on it is removed. This cannot be undone. Type DELETE to confirm.', placeholder: 'DELETE', confirmText: 'Delete account', danger: true });
    if (typed == null) return;
    const pin = S.settings.pinSet
      ? await ui.prompt({ title: 'Parent PIN required', body: 'Deleting the account needs the parent PIN.', type: 'password', placeholder: '4 digits', confirmText: 'Delete account', danger: true })
      : undefined;
    if (S.settings.pinSet && !pin) return;
    try {
      await api('/api/me', { method: 'DELETE', body: JSON.stringify({ confirm: String(typed).trim(), pin }) });
      location.href = '/';
    } catch (e) { toast(e.message, true); }
  };
  document.onkeydown = (e) => {
    if (e.key === 'Escape') { $('modal').classList.remove('open'); stopSpeaking(); }
    // Any text field, not just the talk bar: the journal pad is a whole page of
    // typing and "m" or "n" in it used to reach the shot marker.
    const el = document.activeElement;
    if (el && (el.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName))) return;
    if (e.key === 'm') mark(true);
    if (e.key === 'n') mark(false);
  };

  // Modules mount last so they can take over anything the shell wired above
  // (the sign-out button becomes a real sign out once accounts are in play).
  mountModules();
  // Body Map: movement reps ride with the snapshots (Dribbling and Footwork tabs),
  // and "Try this drill" opens the Skills view on the drill's skill.
  posemetrics.onRep?.((rep) => { if (S.running) queueRep(S.sessionId, rep); });
  document.addEventListener('bodymap:drill', (e) => { go('skills'); skills.open?.(e.detail?.skill); });
  // The Worker stores this so "tomorrow at five" and the nutrition day
  // boundaries mean the player's day, not UTC. Cheap, and it must not block boot.
  api(`/api/events?tz=${new Date().getTimezoneOffset()}`).catch(() => {});
  renderSettings();
  renderDashboard();
  $('dock').classList.add('min');

  // Last, so it wins over whatever the markup had open.
  const asked = demoMode ? 'train' : requestedView();
  if (asked) go(asked);
  paintDemo();

})();
